import {
  IContactIntelligence,
  INSIGHT_CATEGORY,
  INSIGHT_STATUS,
  ISocialPrompt,
  SOCIAL_CONTENT_TYPE,
  MESSAGE_FORMAT,
  IAvailableSPVoice,
  ISocialPromptSeriesReadWithStatus,
  SocialPromptSeriesKind,
  VOICE_KEY,
} from "../../../Types/account";
import {
  getDisplayableContactEmail,
  hasDisplayableContactEmail,
} from "../../../utils/contactEnrichmentHelpers";
import { Fragment, useEffect, useState, useMemo, useRef, useCallback } from "react";
import { ContactEmailWithCaution } from "../ContactEmailWithCaution";
import { SocialPromptRevisionPanel } from "../SocialPromptRevisionPanel";
import CustomDropdown from "../CustomDropDown";
import { updateSocialPrompt } from "../../../Api/updateSocialPrompt";
import { deleteSocialPromptSeries } from "../../../Api/deleteSocialPromptSeries";
import { formatEnumValue } from "../../../utils/formatters";
import { getUserFromLS } from "../../../utils/getUserFromLS";
import { USER_ROLE } from "../../../Types/user";
import { updateInsightStatus } from "../../../Api/updateInsightStatus";
import { getContactsPerAccount } from "../../../Api/getContactsPerAccount";
import { getSocialPromptsForInsight } from "../../../Api/getSocialPromptsForInsight";
import { getSocialPromptSeries } from "../../../Api/getSocialPromptSeries";
import { getSpecificSocialPrompt } from "../../../Api/getSpecificSocialPrompt";
import { getAvailableSPVoices } from "../../../Api/getAvailableSPVoices";
import {
  requestSocialPrompt,
  IAccountInsightSPRequest,
  IIndustryInsightSPRequest,
  IInsightsItemSPRequest,
  IContactIntelligenceSPRequest,
  IAssetSPRequest,
  createCadenceStep,
  MAX_CADENCE_STEPS,
} from "../../../Api/requestSocialPrompt";
import { publishUserEvent } from "../../../Api/publishUserEvent";
import { compareContacts } from "../../../utils/sortHelpers";
import { LINKEDIN_ENGAGEMENT_TYPE, USER_ACTION_TYPE, USER_EVENT_TYPE, UserEvent } from "../../../Types/userEvents";
import {
  enrichPromptsWithSeriesContext,
  flattenSeriesToPrompts,
  formatSeriesKind,
  getPromptStepLabel,
  getPromptStepNumber,
  getSeriesChannelSummary,
  getSeriesStepsMeta,
  formatSeriesCreatedAt,
  sortPromptsByStep,
  sortSeriesByMostRecent,
} from "../../../utils/socialPromptHelpers";
import { GlassModalHeader } from "./GlassModalHeader";
import { resolveUserEventClientId } from "../../../utils/userEventClientId";
import { SocialPromptMessageEditor } from "../SocialPromptMessageEditor";
import {
  buildSocialPromptClipboard,
  copySocialPromptToClipboard,
  decodePromptText,
  getPromptMessageFieldLabel,
  promptSupportsSubjectField,
  shouldIncludeSubjectInClipboard,
} from "../../../utils/socialPromptMessageHelpers";
import {
  CadenceStepEditor,
  CadenceStepUi,
  createDefaultCadenceStep,
  generateCadenceStepId,
  getCadenceBuilderHint,
} from "../CadenceStepEditor";
import { ICadenceTemplateSummary } from "../../../Types/socialPromptCadenceTemplate";
import { getSocialPromptCadenceTemplates } from "../../../Api/getSocialPromptCadenceTemplates";
import { getSocialPromptCadenceTemplate } from "../../../Api/getSocialPromptCadenceTemplate";
import { createSocialPromptCadenceTemplate } from "../../../Api/createSocialPromptCadenceTemplate";
import { updateSocialPromptCadenceTemplate } from "../../../Api/updateSocialPromptCadenceTemplate";
import { getApiErrorMessage, getApiErrorStatus } from "../../../utils/getApiErrorMessage";
import {
  fetchWorkspaceInsightContext,
  type WorkspaceInsightContext,
} from "../../../utils/workspaceInsightContext";
import { useClientCapabilities } from "../../../hooks/useClientCapabilities";
import { CAPABILITY_KEYS } from "../../../utils/capabilities";
import "../../../Css/Outreach/OutreachWorkspace.css";
import "../../../Css/Outreach/OutreachWorkspace.glass.css";

function getSocialPromptGlassModifier(
  category: INSIGHT_CATEGORY | "CONTACT_INTELLIGENCE" | "INSIGHTS_ITEM" | null
): string {
  switch (category) {
    case INSIGHT_CATEGORY.INDUSTRY:
      return "glassModal_shell--industry";
    case INSIGHT_CATEGORY.ASSET:
      return "glassModal_shell--asset";
    case INSIGHT_CATEGORY.ACCOUNT:
    case "CONTACT_INTELLIGENCE":
      return "glassModal_shell--account";
    default:
      return "glassModal_shell--account";
  }
}

function getSocialPromptKicker(
  category: INSIGHT_CATEGORY | "CONTACT_INTELLIGENCE" | "INSIGHTS_ITEM" | null
): string {
  switch (category) {
    case INSIGHT_CATEGORY.INDUSTRY:
      return "Industry insight";
    case INSIGHT_CATEGORY.ASSET:
      return "Asset insight";
    case INSIGHT_CATEGORY.ACCOUNT:
      return "Account insight";
    case "CONTACT_INTELLIGENCE":
      return "Contact";
    default:
      return "Outreach";
  }
}

function getContentTypeIconClass(
  contentType: SOCIAL_CONTENT_TYPE | null | undefined
): string {
  switch (contentType) {
    case SOCIAL_CONTENT_TYPE.EMAIL:
      return "fa-regular fa-envelope";
    case SOCIAL_CONTENT_TYPE.CALL:
      return "fa-solid fa-phone";
    case SOCIAL_CONTENT_TYPE.LINKEDIN_DM:
    case SOCIAL_CONTENT_TYPE.LINKEDIN_POST:
      return "fa-brands fa-linkedin";
    case SOCIAL_CONTENT_TYPE.BLOG:
      return "fa-solid fa-pen-nib";
    default:
      return "fa-regular fa-message";
  }
}

function getSendButtonLabel(contact: IContactIntelligence | null | undefined): string {
  const name = contact?.name?.trim();
  return name ? `Send - ${name}` : "Send";
}

export type SocialPromptModalProps = {
  socialPromptId: number | null;
  insightId: number | null;
  origin?: { kind: "INSIGHTS_ITEM"; id: string };
  category: INSIGHT_CATEGORY | "CONTACT_INTELLIGENCE" | "INSIGHTS_ITEM" | null;
  onUpdateCompletedSocialPrompts: (promptId: number) => void;
  onClose: () => void;
  accountId: string | null;
  insightClientId?: string | null;
  insightIndustryId?: string | null;
  possibleContacts: IContactIntelligence[] | null;
  /** Deep link / notification: after series load, select this series (and matching author filter). */
  initialSeriesId?: string | null;
  /** Centered modal (default) or full workspace overlay. */
  presentation?: "modal" | "workspace";
  /** Pre-loaded workspace header context from the launching page. */
  insightContext?: WorkspaceInsightContext | null;
  initialMode?: "new";
  initialStepNumber?: number | null;
  /** Tour / host-controlled page (keeps the workspace mounted across compose ↔ request). */
  forcePage?: "prompts" | "request";
  /** Allow this entry point to persist prompt copy edits. Backend access remains authoritative. */
  allowPromptSave?: boolean;
};

/** Matches OutreachWorkspace.css stacked breakpoint — compact focus chrome below this width. */
const COMPACT_OUTREACH_VIEWPORT_QUERY = "(max-width: 1400px)";

const useCompactOutreachViewport = (): boolean => {
  const [isCompact, setIsCompact] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(COMPACT_OUTREACH_VIEWPORT_QUERY).matches
      : false
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;

    const mediaQuery = window.matchMedia(COMPACT_OUTREACH_VIEWPORT_QUERY);
    const updateMatch = () => setIsCompact(mediaQuery.matches);
    updateMatch();
    mediaQuery.addEventListener("change", updateMatch);
    return () => mediaQuery.removeEventListener("change", updateMatch);
  }, []);

  return isCompact;
};

export const SocialPromptModal: React.FC<SocialPromptModalProps> = ({
  socialPromptId,
  insightId,
  origin,
  category,
  onUpdateCompletedSocialPrompts,
  onClose,
  accountId,
  insightClientId = null,
  insightIndustryId = null,
  possibleContacts,
  initialSeriesId = null,
  presentation = "modal",
  insightContext: initialInsightContext = null,
  initialMode,
  initialStepNumber = null,
  forcePage,
  allowPromptSave = false,
}) => {
  const anchorId = origin?.id ?? insightId;
  const user = getUserFromLS();
  const adminAccess = user.role === USER_ROLE.ADMIN;
  const isSalesLeader = user.role === USER_ROLE.SALES_LEADER;
  const isClientAdmin = user.role === USER_ROLE.CLIENT_ADMIN;
  const { hasCapability } = useClientCapabilities();
  const cadencesEnabled = hasCapability(CAPABILITY_KEYS.CADENCES);
  const cadenceMaxSteps = cadencesEnabled ? undefined : 1;
  const cadenceBuilderHint = getCadenceBuilderHint({
    excludeSoloChannels: category === "CONTACT_INTELLIGENCE",
    singleStep: !cadencesEnabled,
  });
  const isCompactViewport = useCompactOutreachViewport();
  const isCompactOutreach = presentation === "workspace" && isCompactViewport;

  const [insightContext, setInsightContext] = useState<WorkspaceInsightContext | null>(
    initialInsightContext
  );
  const [insightContextLoading, setInsightContextLoading] = useState(
    presentation === "workspace" &&
      !initialInsightContext &&
      insightId != null &&
      Boolean(category)
  );

  const processContacts = (contacts: IContactIntelligence[]) => {
    return contacts
      .filter(contact => contact.insight_interaction_status.insight_status !== INSIGHT_STATUS.DISMISSED)
      .sort(compareContacts);
  };

  const [localPossibleContacts, setLocalPossibleContacts] = useState<IContactIntelligence[] | null>(() => {
    if (possibleContacts == null) return null;
    return possibleContacts.length > 0 ? processContacts(possibleContacts) : [];
  });

  const [socialPromptSeries, setSocialPromptSeries] = useState<ISocialPromptSeriesReadWithStatus[]>([]);
  const [promptDataResolved, setPromptDataResolved] = useState(
    () => !((anchorId != null && category != null) || socialPromptId != null)
  );
  const [promptViewReady, setPromptViewReady] = useState(false);
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(null);
  const [selectedStepNumber, setSelectedStepNumber] = useState<number | null>(null);
  const [isSequenceChooserOpen, setIsSequenceChooserOpen] = useState(false);
  const [showEntryChoices, setShowEntryChoices] = useState(
    presentation === "workspace" &&
      initialMode !== "new" &&
      !forcePage &&
      initialSeriesId == null &&
      socialPromptId == null &&
      initialStepNumber == null
  );
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  const authorLabelForSeries = useCallback(
    (series: ISocialPromptSeriesReadWithStatus): string => {
      const uid = series.user_id;
      if (!uid) return "Unassigned";
      if (user?.id && uid === user.id) {
        return user.full_name || user.name || "You";
      }
      return `User ${uid.slice(0, 8)}…`;
    },
    [user]
  );

  const prompts = useMemo(
    () => flattenSeriesToPrompts(socialPromptSeries),
    [socialPromptSeries]
  );

  /** Fetch prompts when component loads */
  useEffect(() => {
    if (anchorId != null && category) {
      setPromptDataResolved(false);
      setPromptViewReady(false);
      getSocialPromptsForInsight(
        anchorId.toString(),
        category,
        (fetchedSeries) => {
          setSocialPromptSeries(fetchedSeries);
          setPromptDataResolved(true);
        },
        (error) => {
          console.error("Failed to fetch social prompts:", error);
          setPromptDataResolved(true);
        },
        accountId ?? undefined
      );
    } else if (socialPromptId != null) {
      setPromptDataResolved(false);
      setPromptViewReady(false);
      getSpecificSocialPrompt(
        socialPromptId,
        (fetchedPrompt) => {
          const attach = (p: ISocialPrompt): ISocialPrompt => ({
            ...p,
            step_number: getPromptStepNumber(p) ?? p.number_in_series ?? 1,
            total_steps: p.total_steps ?? 1,
            series_kind: p.series_kind ?? SocialPromptSeriesKind.STANDALONE,
            series_id: p.series_id ?? null,
          });
          const p0 = attach(fetchedPrompt);
          if (p0.series_id) {
            getSocialPromptSeries(
              p0.series_id,
              (full) => {
                setSocialPromptSeries([
                  {
                    ...full,
                    prompts: sortPromptsByStep(full.prompts as ISocialPrompt[]),
                  } as ISocialPromptSeriesReadWithStatus,
                ]);
                setPromptDataResolved(true);
              },
              () => {
                setSocialPromptSeries([
                  {
                    id: p0.series_id ?? `single-${p0.id}`,
                    kind: p0.series_kind ?? SocialPromptSeriesKind.STANDALONE,
                    total_steps: p0.total_steps ?? 1,
                    user_id: null,
                    prompts: [p0],
                  },
                ]);
                setPromptDataResolved(true);
              }
            );
          } else {
            setSocialPromptSeries([
              {
                id: `legacy-${p0.id}`,
                kind: p0.series_kind ?? SocialPromptSeriesKind.STANDALONE,
                total_steps: p0.total_steps ?? 1,
                user_id: null,
                prompts: [p0],
              },
            ]);
            setPromptDataResolved(true);
          }
        },
        (error) => {
          console.error("Failed to fetch social prompt:", error);
          setPromptDataResolved(true);
        }
      );
    } else {
      setPromptDataResolved(true);
    }
  }, [anchorId, category, socialPromptId, accountId]);

  useEffect(() => {
    if (possibleContacts != null && possibleContacts.length > 0) {
      setLocalPossibleContacts(processContacts(possibleContacts));
    }

    const normalizedAccountId =
      accountId != null ? String(accountId).trim() : "";
    const shouldFetch =
      Boolean(normalizedAccountId) &&
      (possibleContacts == null || possibleContacts.length === 0);

    if (!shouldFetch) {
      if (possibleContacts != null && possibleContacts.length === 0) {
        setLocalPossibleContacts([]);
      }
      return;
    }

    let cancelled = false;
    getContactsPerAccount(
      normalizedAccountId,
      (response) => {
        if (!cancelled) {
          setLocalPossibleContacts(processContacts(response));
        }
      },
      (error) => {
        console.error("Failed to fetch contacts:", error);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [accountId, possibleContacts]);

  useEffect(() => {
    if (presentation !== "workspace" || anchorId == null || !category) {
      setInsightContext(null);
      setInsightContextLoading(false);
      return;
    }

    if (initialInsightContext) {
      setInsightContext(initialInsightContext);
      setInsightContextLoading(false);
      return;
    }

    if (category === "INSIGHTS_ITEM" || insightId == null) { setInsightContextLoading(false); return; }
    let cancelled = false;
    setInsightContextLoading(true);

    fetchWorkspaceInsightContext(
      insightId,
      category,
      (context) => {
        if (!cancelled) {
          setInsightContext(context);
          setInsightContextLoading(false);
        }
      },
      () => {
        if (!cancelled) {
          setInsightContext(null);
          setInsightContextLoading(false);
        }
      }
    );

    return () => {
      cancelled = true;
    };
  }, [presentation, anchorId, insightId, category, initialInsightContext]);

  // Fetch available voices when component loads
  useEffect(() => {
    getAvailableSPVoices(
      (voices) => {
        const active = voices.filter((voice) => voice.is_active);
        active.sort((a, b) => {
          const aRec = a.key === VOICE_KEY.INTENTICO_RECOMMENDED;
          const bRec = b.key === VOICE_KEY.INTENTICO_RECOMMENDED;
          if (aRec && !bRec) return -1;
          if (!aRec && bRec) return 1;
          return 0;
        });
        setAvailableVoices(active);
        // Don't set a default voice - let user choose
      },
      (error) => {
        console.error("Failed to fetch available voices:", error);
      }
    );
  }, []);


  const [selectedContact, setSelectedContact] = useState<IContactIntelligence | null>(null);

  const filteredSeries = useMemo(
    () => sortSeriesByMostRecent(socialPromptSeries),
    [socialPromptSeries]
  );

  const selectedSeries = useMemo(
    () => filteredSeries.find((s) => s.id === selectedSeriesId) ?? null,
    [filteredSeries, selectedSeriesId]
  );

  const stepsInSelectedSeries = useMemo(() => {
    if (!selectedSeries) return [];
    return enrichPromptsWithSeriesContext(selectedSeries);
  }, [selectedSeries]);

  const selectedPrompt = useMemo(() => {
    if (stepsInSelectedSeries.length === 0) return null;
    if (selectedStepNumber != null) {
      const hit = stepsInSelectedSeries.find((p) => getPromptStepNumber(p) === selectedStepNumber);
      if (hit) return hit;
    }
    return stepsInSelectedSeries[0];
  }, [stepsInSelectedSeries, selectedStepNumber]);

  const stepDropdownOptions = useMemo(
    () =>
      stepsInSelectedSeries.map((p, index) => ({
        value: String(index),
        label: getPromptStepLabel(p),
      })),
    [stepsInSelectedSeries]
  );

  const defaultDropDownLabelIndex = useMemo(() => {
    if (!selectedPrompt || stepsInSelectedSeries.length === 0) return 0;
    const ix = stepsInSelectedSeries.findIndex((p) => p.id === selectedPrompt.id);
    return ix === -1 ? 0 : ix;
  }, [selectedPrompt, stepsInSelectedSeries]);

  // Shared: mark prompt completed in DB and/or local state (used by Mark Complete and action buttons)
  const requestMarkPromptCompleted = useCallback(
    (prompt: ISocialPrompt, onSuccess: () => void, onError: (error: unknown) => void) => {
      updateInsightStatus(
        {
          insight_type: "SOCIAL_PROMPT",
          insight_id: prompt.id,
          new_status: INSIGHT_STATUS.COMPLETED,
          account_id: accountId,
          contact_id: selectedContact?.id || undefined
        },
        onSuccess,
        onError
      );
    },
    [accountId, selectedContact?.id]
  );

  const updateLocalStateAsCompleted = useCallback((prompt: ISocialPrompt) => {
    const updatedPrompt = { ...prompt, interaction_status: { ...prompt.interaction_status, insight_status: INSIGHT_STATUS.COMPLETED } };
    setSocialPromptSeries((prev) =>
      prev.map((s) => ({
        ...s,
        prompts: s.prompts.map((p) => (p.id === updatedPrompt.id ? updatedPrompt : p)),
      }))
    );
    onUpdateCompletedSocialPrompts(prompt.id);
  }, [onUpdateCompletedSocialPrompts]);

  // Ref for the contact list container, used to scroll to the selected contact
  const contactListRef = useRef<HTMLDivElement>(null);
  const contactDisclosureRef = useRef<HTMLDetailsElement>(null);
  const journeyStepsRef = useRef<HTMLElement>(null);
  const [contactCarouselIndex, setContactCarouselIndex] = useState(0);

  const scrollToSelectedContact = useCallback((contactId: number) => {
    if (!contactListRef.current) return;
    const card = contactListRef.current.querySelector(
      `[data-contact-id="${contactId}"]`
    ) as HTMLElement | null;
    if (card && typeof card.scrollIntoView === 'function') {
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  const scrollContactCarouselTo = useCallback(
    (contacts: IContactIntelligence[], index: number) => {
      const nextIndex = Math.max(0, Math.min(index, contacts.length - 1));
      const nextContact = contacts[nextIndex];
      if (!nextContact) return;
      setContactCarouselIndex(nextIndex);
      const card = contactListRef.current?.querySelector<HTMLElement>(
        `[data-contact-id="${nextContact.id}"]`
      );
      card?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    },
    []
  );

  const getAuthorForPrompt = useCallback(
    (prompt: ISocialPrompt) => {
      const s = socialPromptSeries.find((ser) => ser.prompts.some((p) => p.id === prompt.id));
      return s ? authorLabelForSeries(s) : "Unassigned";
    },
    [socialPromptSeries, authorLabelForSeries]
  );

  const currentAuthorLabel = useMemo(() => {
    if (selectedSeries) return authorLabelForSeries(selectedSeries);
    if (selectedPrompt) return getAuthorForPrompt(selectedPrompt);
    return null;
  }, [selectedSeries, selectedPrompt, authorLabelForSeries, getAuthorForPrompt]);

  const isChecked = selectedPrompt?.interaction_status.insight_status === INSIGHT_STATUS.COMPLETED;

  // Determine initial page based on whether prompts exist
  useEffect(() => {
    if (forcePage) {
      setShowEntryChoices(false);
      setPromptViewReady(true);
      return;
    }
    if (initialMode === "new") {
      setShowEntryChoices(false);
      setCurrentPage("request");
      setPromptViewReady(true);
      return;
    }
    if (!promptDataResolved) return;
    if (prompts.length === 0 && anchorId && category) {
      setCurrentPage('request');
    } else if (prompts.length > 0) {
      setCurrentPage('prompts');
    }
    setPromptViewReady(true);
  }, [prompts.length, anchorId, category, initialMode, forcePage, promptDataResolved]);

  useEffect(() => {
    if (!forcePage) return;
    setShowEntryChoices(false);
    setCurrentPage(forcePage);
  }, [forcePage]);

  useEffect(() => {
    if (initialStepNumber == null || stepsInSelectedSeries.length === 0) return;
    const hit = stepsInSelectedSeries.find(
      (prompt) => getPromptStepNumber(prompt) === initialStepNumber
    );
    if (hit) {
      setSelectedStepNumber(initialStepNumber);
    }
  }, [initialStepNumber, stepsInSelectedSeries]);

  useEffect(() => {
    if (filteredSeries.length === 0) {
      setSelectedSeriesId(null);
      setSelectedStepNumber(null);
      return;
    }
    const stillValid = selectedSeriesId && filteredSeries.some((s) => s.id === selectedSeriesId);
    if (!stillValid) {
      const preferred =
        initialSeriesId &&
        filteredSeries.some((s) => s.id === initialSeriesId)
          ? filteredSeries.find((s) => s.id === initialSeriesId)!
          : filteredSeries[0];
      setSelectedSeriesId(preferred.id);
      setSelectedStepNumber(getPreferredStepNumber(preferred));
    }
  }, [filteredSeries, selectedSeriesId, initialSeriesId]);

  useEffect(() => {
    if (!socialPromptId || prompts.length === 0) return;
    const p = prompts.find((x) => x.id === socialPromptId);
    if (!p?.series_id) return;
    setSelectedSeriesId(p.series_id);
    setSelectedStepNumber(getPromptStepNumber(p) ?? 1);
  }, [socialPromptId, prompts]);

  useEffect(() => {
    if (!selectedSeries) return;
    const assignedContactId =
      selectedSeries.contact_id ??
      selectedSeries.prompts.find((prompt) => prompt.contact_id != null)?.contact_id ??
      null;
    const assignedContact = assignedContactId
      ? localPossibleContacts?.find((contact) => contact.id === assignedContactId) ?? null
      : null;
    setSelectedContact(assignedContact);
  }, [selectedSeries?.id, selectedSeries?.contact_id, localPossibleContacts]);


  const getStatusIcon = (status: INSIGHT_STATUS, isFavorite: boolean) => {
    // Show both favorite star and insight status
    return (
      <div className="status-indicators">
        {/* Favorite indicator */}
        {isFavorite && (
          <i className="fa-duotone fa-solid fa-star contact-status-icon favorite" />
        )}
        
        {/* Insight status indicator */}
        <i className={`fa-duotone fa-solid ${getInsightStatusIcon(status)} contact-status-icon ${getInsightStatusClass(status)}`} />
      </div>
    );
  };

  const getInsightStatusIcon = (status: INSIGHT_STATUS) => {
    switch (status) {
      case INSIGHT_STATUS.COMPLETED:
        return "fa-check-circle";
      case INSIGHT_STATUS.DISMISSED:
        return "fa-times-circle";
      case INSIGHT_STATUS.SEEN:
        return "fa-circle";
      default:
        return "fa-circle";
    }
  };

  const getInsightStatusClass = (status: INSIGHT_STATUS) => {
    switch (status) {
      case INSIGHT_STATUS.COMPLETED:
        return "completed";
      case INSIGHT_STATUS.DISMISSED:
        return "dismissed";
      case INSIGHT_STATUS.SEEN:
        return "seen";
      default:
        return "not-seen";
    }
  };

  const getStatusClass = (status: INSIGHT_STATUS, isFavorite: boolean) => {
    // Combine favorite and insight status classes
    let classes: string[] = [];
    
    if (isFavorite) {
      classes.push("contact-favorite");
    }
    
    switch (status) {
      case INSIGHT_STATUS.COMPLETED:
        classes.push("contact-completed");
        break;
      case INSIGHT_STATUS.DISMISSED:
        classes.push("contact-dismissed");
        break;
      case INSIGHT_STATUS.SEEN:
        classes.push("contact-seen");
        break;
      default:
        classes.push("contact-not-seen");
        break;
    }
    
    return classes.join(" ");
  };

  const isContactCompatibleWithPromptType = (
    contact: IContactIntelligence,
    promptType: string
  ): boolean => {
    switch (promptType) {
      case SOCIAL_CONTENT_TYPE.EMAIL:
        return hasDisplayableContactEmail(contact);
      case SOCIAL_CONTENT_TYPE.CALL:
        return !!(contact.mobile_phone_number || contact.direct_phone_number || contact.corporate_phone_number);
      case SOCIAL_CONTENT_TYPE.LINKEDIN_DM:
        return !!contact.linkedin_url;
      case SOCIAL_CONTENT_TYPE.LINKEDIN_POST:
      case SOCIAL_CONTENT_TYPE.BLOG:
        return true; // Posts and blogs are not contact-specific
      default:
        return true;
    }
  };

  const isContactCompatibleWithCadence = (
    contact: IContactIntelligence,
    steps: CadenceStepUi[]
  ): boolean => {
    return steps.every((step) =>
      isContactCompatibleWithPromptType(contact, step.content_type)
    );
  };

  const getIncompatibleLabel = (promptType: string): string => {
    switch (promptType) {
      case SOCIAL_CONTENT_TYPE.EMAIL:
        return "No email";
      case SOCIAL_CONTENT_TYPE.CALL:
        return "No phone";
      case SOCIAL_CONTENT_TYPE.LINKEDIN_DM:
        return "No LinkedIn";
      default:
        return "";
    }
  };

  const getCadenceIncompatibleLabel = (
    contact: IContactIntelligence,
    steps: CadenceStepUi[]
  ): string => {
    const labels: string[] = [];
    const seen = new Set<string>();
    for (const step of steps) {
      if (!isContactCompatibleWithPromptType(contact, step.content_type)) {
        const label = getIncompatibleLabel(step.content_type);
        if (label && !seen.has(label)) {
          seen.add(label);
          labels.push(label);
        }
      }
    }
    return labels.join(" · ");
  };

  const isContactCompatibleForChannel = (
    contact: IContactIntelligence,
    cadenceOrPromptType: CadenceStepUi[] | string
  ): boolean =>
    typeof cadenceOrPromptType === "string"
      ? isContactCompatibleWithPromptType(contact, cadenceOrPromptType)
      : isContactCompatibleWithCadence(contact, cadenceOrPromptType);

  const getContactsOrderedForChannel = (
    cadenceOrPromptType: CadenceStepUi[] | string
  ): IContactIntelligence[] => {
    if (!localPossibleContacts?.length) return [];

    const compatible: IContactIntelligence[] = [];
    const incompatible: IContactIntelligence[] = [];
    for (const contact of localPossibleContacts) {
      if (isContactCompatibleForChannel(contact, cadenceOrPromptType)) {
        compatible.push(contact);
      } else {
        incompatible.push(contact);
      }
    }
    return [...compatible, ...incompatible];
  };

  const handleContactSelect = (
    contact: IContactIntelligence,
    cadenceOrPromptType: CadenceStepUi[] | string
  ) => {
    if (!isContactCompatibleForChannel(contact, cadenceOrPromptType)) return;
    const isDeselecting = selectedContact?.id === contact.id;
    setSelectedContact(isDeselecting ? null : contact);
    if (!isDeselecting && contactDisclosureRef.current) {
      contactDisclosureRef.current.open = false;
    }
  };

  const renderContactCard = (
    contact: IContactIntelligence,
    cadenceOrPromptType: CadenceStepUi[] | string
  ) => {
    const isSelected = selectedContact?.id === contact.id;
    const isCompatible = isContactCompatibleForChannel(
      contact,
      cadenceOrPromptType
    );
    const incompatibleLabel =
      !isCompatible
        ? typeof cadenceOrPromptType === "string"
          ? getIncompatibleLabel(cadenceOrPromptType)
          : getCadenceIncompatibleLabel(contact, cadenceOrPromptType)
        : "";
    const displayableEmail = getDisplayableContactEmail(contact);
    return (
      <div 
        key={contact.id}
        data-contact-id={contact.id}
        data-contact-compatible={isCompatible ? "true" : "false"}
        className={`socialPromptModal_contactCard ${isSelected ? 'selected' : ''} ${!isCompatible ? 'contact-incompatible' : ''} ${getStatusClass(contact.insight_interaction_status.insight_status, contact.insight_interaction_status.favorite)}`}
        onClick={
          isCompatible
            ? () => handleContactSelect(contact, cadenceOrPromptType)
            : undefined
        }
        aria-disabled={!isCompatible}
        tabIndex={isCompatible ? 0 : -1}
      >
        <div className="socialPromptModal_contactCardHeader">
          <div className="socialPromptModal_contactName">
            {contact.name}
            {contact.title && <span className="socialPromptModal_contactTitle"> - {contact.title}</span>}
          </div>
          <div className="socialPromptModal_contactStatus">
            {getStatusIcon(contact.insight_interaction_status.insight_status, contact.insight_interaction_status.favorite)}
            {isSelected && <i className="fa-solid fa-check"></i>}
          </div>
        </div>
        <div className="socialPromptModal_contactCardDetails">
          {displayableEmail && (
            <div className="socialPromptModal_contactDetail">
              <i className="fa-regular fa-envelope"></i>
              <ContactEmailWithCaution
                email={displayableEmail}
                emailStatus={contact.email_status}
              />
            </div>
          )}
          {(contact.mobile_phone_number || contact.direct_phone_number || contact.corporate_phone_number) && (
            <div className="socialPromptModal_contactDetail">
              <i className="fa-regular fa-phone"></i>
              <span>
                {contact.mobile_phone_number || 
                 contact.direct_phone_number || 
                 contact.corporate_phone_number}
              </span>
            </div>
          )}
          {contact.linkedin_url && (
            <div className="socialPromptModal_contactDetail">
              <i className="fa-brands fa-linkedin"></i>
              <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                LinkedIn Profile
              </a>
            </div>
          )}
          {incompatibleLabel && (
            <div className="socialPromptModal_incompatibleLabel">{incompatibleLabel}</div>
          )}
        </div>
      </div>
    );
  };

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [notification, setNotification] = useState<{type: 'success' | 'error' | 'info', message: string} | null>(null);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [showDeleteSeriesConfirm, setShowDeleteSeriesConfirm] = useState(false);
  const [isDeletingSeries, setIsDeletingSeries] = useState(false);
  
  // Page navigation state
  const [currentPage, setCurrentPage] = useState<'prompts' | 'request'>(
    forcePage ?? 'prompts'
  );
  const [requestWizardStep, setRequestWizardStep] = useState<1 | 2 | 3>(1);
  const requestWizardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (requestWizardRef.current) {
      requestWizardRef.current.scrollTop = 0;
    }
  }, [requestWizardStep]);

  // A contact-intelligence request targets one explicit contact. Never fall back to an
  // unrelated first result if that contact is absent from the available list.
  useEffect(() => {
    if (currentPage !== "request") return;
    if (category !== "CONTACT_INTELLIGENCE") return;
    if (!localPossibleContacts?.length) return;
    const preferred =
      insightId != null
        ? localPossibleContacts.find((contact) => contact.id === insightId) ?? null
        : null;
    setSelectedContact((prev) => {
      if (prev && localPossibleContacts.some((c) => c.id === prev.id)) return prev;
      return preferred;
    });
  }, [currentPage, category, localPossibleContacts, insightId]);

  // Request more prompts state
  const [availableVoices, setAvailableVoices] = useState<IAvailableSPVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>("");
  const [sequenceTitle, setSequenceTitle] = useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  const [cadenceSteps, setCadenceSteps] = useState<CadenceStepUi[]>(() => [
    {
      id: generateCadenceStepId(),
      content_type: SOCIAL_CONTENT_TYPE.EMAIL,
      objective: "",
    },
  ]);
  const [isRequesting, setIsRequesting] = useState(false);

  // Saved cadence templates (private, per-user)
  const [templates, setTemplates] = useState<ICadenceTemplateSummary[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false);
  const [saveTemplateTitle, setSaveTemplateTitle] = useState("");
  const [saveTemplateError, setSaveTemplateError] = useState<string | null>(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [duplicateTemplateId, setDuplicateTemplateId] = useState<string | null>(null);

  const loadCadenceTemplates = useCallback(() => {
    getSocialPromptCadenceTemplates()
      .then((list) => setTemplates(list))
      .catch((error) => {
        console.error("Failed to load cadence templates:", error);
      });
  }, []);

  // Load saved templates on mount
  useEffect(() => {
    loadCadenceTemplates();
  }, [loadCadenceTemplates]);

  const createEventId = () => {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  const getInsightCategoryForUserEvent = (): INSIGHT_CATEGORY | null => {
    if (
      category === INSIGHT_CATEGORY.ACCOUNT ||
      category === INSIGHT_CATEGORY.INDUSTRY ||
      category === INSIGHT_CATEGORY.ASSET
    ) {
      return category;
    }
    return null;
  };

  const buildUserActionEvent = (trigger: "mark-complete" | "send" | "copy" | "linkedin-open"): UserEvent | null => {
    if (!selectedPrompt || !selectedPrompt.content_type) {
      return null;
    }

    const baseEvent = {
      eventId: createEventId(),
      eventType: USER_EVENT_TYPE.USER_ACTION as const,
      userId: user?.id ?? null,
      accountId: accountId ?? null,
      clientId: resolveUserEventClientId(insightClientId, selectedContact, user?.client_id) ?? "",
      contactId: selectedContact?.id ?? null,
      industryId: insightIndustryId ?? null,
      metadata: {
        source_service: "intentico-react-app",
        version: null
      },
      occurredAt: new Date()
    };

    const commonPayloadFields = {
      plannedActionId: selectedPrompt.id,
      socialPromptId: selectedPrompt.id,
      numberInSeries: getPromptStepNumber(selectedPrompt) ?? selectedPrompt.number_in_series ?? null,
      toContactId: selectedContact?.id ?? null,
      toContactName: selectedContact?.name ?? null,
      toContactTitle: selectedContact?.title ?? null,
      activityId: null,
      insightId: insightId ?? null,
      insightCategory: getInsightCategoryForUserEvent(),
      markedManually: trigger === "mark-complete"
    };

    switch (selectedPrompt.content_type) {
      case SOCIAL_CONTENT_TYPE.EMAIL:
        return {
          ...baseEvent,
          payload: {
            actionType: USER_ACTION_TYPE.EMAIL_SENT,
            subject: subject || "",
            message: message || "",
            ...commonPayloadFields
          }
        };
      case SOCIAL_CONTENT_TYPE.CALL:
        return {
          ...baseEvent,
          payload: {
            actionType: USER_ACTION_TYPE.CALL_MADE,
            callScript: message || "",
            outcome: null,
            responseSummary: null,
            sentiment: null,
            ...commonPayloadFields
          }
        };
      case SOCIAL_CONTENT_TYPE.LINKEDIN_DM:
        return {
          ...baseEvent,
          payload: {
            actionType: USER_ACTION_TYPE.LINKEDIN_ENGAGEMENT,
            messageType: LINKEDIN_ENGAGEMENT_TYPE.DM,
            engagementMessage: message || "",
            post_url: selectedContact?.linkedin_url ?? null,
            ...commonPayloadFields
          }
        };
      case SOCIAL_CONTENT_TYPE.LINKEDIN_POST:
        return {
          ...baseEvent,
          payload: {
            actionType: USER_ACTION_TYPE.LINKEDIN_ENGAGEMENT,
            messageType: LINKEDIN_ENGAGEMENT_TYPE.POST,
            engagementMessage: message || "",
            post_url: null,
            ...commonPayloadFields
          }
        };
      case SOCIAL_CONTENT_TYPE.BLOG:
        return {
          ...baseEvent,
          payload: {
            actionType: USER_ACTION_TYPE.BLOG_PUBLISHED,
            engagementMessage: message || "",
            ...commonPayloadFields
          }
        };
      default:
        return null;
    }
  };

  const publishSelectedPromptEvent = (trigger: "mark-complete" | "send" | "copy" | "linkedin-open") => {
    const event = buildUserActionEvent(trigger);
    if (!event) return;
    publishUserEvent(event, undefined, (error) => {
      console.warn(`Failed to publish user event (${trigger})`, error);
    });
  };

  const runMarkPromptComplete = useCallback(() => {
    if (!selectedPrompt) return;
    requestMarkPromptCompleted(
      selectedPrompt,
      () => {
        updateLocalStateAsCompleted(selectedPrompt);
        publishSelectedPromptEvent("mark-complete");
      },
      () => {
        setNotification({ type: "error", message: "Failed to complete outreach prompt" });
        setTimeout(() => setNotification(null), 5000);
      }
    );
  }, [selectedPrompt, requestMarkPromptCompleted, updateLocalStateAsCompleted]);

  const handleCheckboxChange = () => {
    if (!selectedPrompt || isChecked) return;
    if (localPossibleContacts && localPossibleContacts.length > 0 && !selectedContact) {
      setCompleteConfirmOpen(true);
      return;
    }
    runMarkPromptComplete();
  };

  // Tooltip state
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    text: string;
    x: number;
    y: number;
  }>({
    visible: false,
    text: '',
    x: 0,
    y: 0
  });

  // Tooltip handlers
  const showTooltip = (event: React.MouseEvent, text: string) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      text,
      x: rect.left  + rect.width + 32,
      y: rect.top - 35
    });
  };

  const hideTooltip = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const handleSubjectChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSubject(event.target.value);
  };

  const renderSubjectField = () => {
    if (!selectedPrompt || !promptSupportsSubjectField(selectedPrompt.content_type)) {
      return null;
    }

    return (
      <div className="socialPromptModal_composeField">
        <label
          className="socialPromptModal_composeFieldLabel"
          htmlFor={selectedPrompt.id + "subject"}
        >
          Subject
        </label>
        <input
          type="text"
          id={selectedPrompt.id + "subject"}
          className="socialPromptModal_composeFieldInput"
          value={subject}
          onChange={handleSubjectChange}
          placeholder="Subject line"
        />
      </div>
    );
  };

  const handleCopyMessage = async (emitUserEvent = true) => {
    if (emitUserEvent && selectedPrompt) {
      requestMarkPromptCompleted(selectedPrompt, () => {}, () => {});
      updateLocalStateAsCompleted(selectedPrompt);
    }
    try {
      const payload = buildSocialPromptClipboard({
        subject: shouldIncludeSubjectInClipboard(selectedPrompt?.content_type)
          ? subject
          : null,
        message,
        messageFormat: selectedPrompt?.message_format ?? MESSAGE_FORMAT.TEXT,
      });
      if (!payload) {
        setNotification({ type: "error", message: "Nothing to copy" });
        setTimeout(() => setNotification(null), 3000);
        return;
      }
      const result = await copySocialPromptToClipboard(
        payload,
        selectedPrompt?.content_type
      );
      if (result === "failed") {
        throw new Error("clipboard write failed");
      }
      setNotification({ type: "success", message: "Copied!" });
      setTimeout(() => setNotification(null), 3000);
      if (emitUserEvent) {
        publishSelectedPromptEvent("copy");
      }
    } catch (err) {
      setNotification({ type: "error", message: "Failed to copy message" });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  // Scroll to the selected contact card whenever the selection changes
  useEffect(() => {
    if (selectedContact) {
      scrollToSelectedContact(selectedContact.id);
    }
  }, [selectedContact, scrollToSelectedContact]);

  // When selected prompt changes, sync subject/message fields
  useEffect(() => {
    if (selectedPrompt) {
      setSubject(decodePromptText(selectedPrompt.subject || ""));
      setMessage(
        selectedPrompt.message_format === MESSAGE_FORMAT.MARKDOWN
          ? selectedPrompt.message || ""
          : decodePromptText(selectedPrompt.message || "")
      );
    } else {
      setSubject("");
      setMessage("");
    }
  }, [selectedPrompt]);

  useEffect(() => {
    const activeStep = journeyStepsRef.current?.querySelector<HTMLElement>(
      '[aria-current="step"]'
    );
    activeStep?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [selectedPrompt?.id]);

  const handleSave = () => {
    if (selectedPrompt && allowPromptSave) {
      setNotification(null);
      const updatedPrompt = { ...selectedPrompt, subject, message };
      updateSocialPrompt(
        updatedPrompt,
        () => {
          setSocialPromptSeries((prev) =>
            prev.map((s) => ({
              ...s,
              prompts: s.prompts.map((p) => (p.id === updatedPrompt.id ? updatedPrompt : p)),
            }))
          );
          setNotification({type: 'success', message: "Social prompt saved successfully!"});
          setTimeout(() => setNotification(null), 3000);
        },
        () => {
          setNotification({type: 'error', message: "Failed to save outreach prompt"});
          setTimeout(() => setNotification(null), 5000);
        }
      );
    }
  };

  const handleDelete = () => {
    if (selectedSeriesId) {
      setShowDeleteSeriesConfirm(true);
    }
  };

  const handleConfirmDeleteSeries = () => {
    if (!selectedSeriesId) return;

    setNotification(null);
    setIsDeletingSeries(true);
    deleteSocialPromptSeries(
      selectedSeriesId,
      () => {
        setSocialPromptSeries((prev) => prev.filter((s) => s.id !== selectedSeriesId));
        setShowDeleteSeriesConfirm(false);
        setIsDeletingSeries(false);
          setNotification({ type: "success", message: "Outreach sequence deleted successfully" });
        setTimeout(() => setNotification(null), 3000);
      },
      () => {
        setIsDeletingSeries(false);
        setNotification({ type: "error", message: "Failed to delete outreach series" });
        setTimeout(() => setNotification(null), 5000);
      }
    );
  };

  const handleSend = () => {
    if (selectedPrompt) {
      requestMarkPromptCompleted(selectedPrompt, () => {}, () => {});
      updateLocalStateAsCompleted(selectedPrompt);
      const recipientEmail = selectedContact ? getDisplayableContactEmail(selectedContact) : null;
      const to = recipientEmail ? `?to=${encodeURIComponent(recipientEmail)}` : '?';
      const mailtoLink = `mailto:${to}&subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(message)}`;
      window.open(mailtoLink, "_blank");
      publishSelectedPromptEvent("send");
    }
  };

  const handleOpenLinkedin = async (isDm: boolean) => {
    if (selectedPrompt) {
      requestMarkPromptCompleted(selectedPrompt, () => {}, () => {});
      updateLocalStateAsCompleted(selectedPrompt);
      if (isDm) {
        // If a contact is selected and has a LinkedIn URL, open their profile and copy message to clipboard
        if (selectedContact?.linkedin_url) {
          // Copy message to clipboard so user can paste it after opening the profile
          await handleCopyMessage(false);
        
          // Open the contact's LinkedIn profile page
          setTimeout(() => {
            window.open(selectedContact.linkedin_url || "", "_blank");
          }, 500);
        } else {
          // No contact selected or no LinkedIn URL - use compose URL with body parameter
          const url = new URL('https://www.linkedin.com/messaging/compose');
          url.searchParams.set('body', message);
          window.open(url.toString(), "_blank");
        }
      } else {
        // For LinkedIn Posts, copy message to clipboard first, then open LinkedIn feed
        await handleCopyMessage(false);
        
        // Open the LinkedIn feed share dialog
        setTimeout(() => {
          window.open('https://www.linkedin.com/feed/?shareActive=true', "_blank");
        }, 500);
      }
      publishSelectedPromptEvent("linkedin-open");
    }
  };

  const getPreferredStepNumber = (series: ISocialPromptSeriesReadWithStatus) => {
    const ordered = sortPromptsByStep(series.prompts);
    const firstIncomplete = ordered.find(
      (prompt) => prompt.interaction_status?.insight_status !== INSIGHT_STATUS.COMPLETED
    );
    return getPromptStepNumber(firstIncomplete ?? ordered[ordered.length - 1]) ?? 1;
  };

  const handleSeriesClick = (seriesId: string) => {
    setNotification(null);
    setSelectedSeriesId(seriesId);
    const s = socialPromptSeries.find((x) => x.id === seriesId);
    if (s) {
      setSelectedStepNumber(getPreferredStepNumber(s));
    }
  };

  const getSeriesDisplayTitle = (series: ISocialPromptSeriesReadWithStatus) => {
    return series.title?.trim() || formatSeriesKind(series.kind);
  };

  const resolveContactFromSeries = (
    series: ISocialPromptSeriesReadWithStatus,
    contacts: IContactIntelligence[] | null
  ): IContactIntelligence | null => {
    if (!series.contact_id || !contacts?.length) return null;
    return contacts.find((contact) => contact.id === series.contact_id) ?? null;
  };

  const getSeriesContactLabel = (series: ISocialPromptSeriesReadWithStatus) => {
    const contact = resolveContactFromSeries(series, localPossibleContacts ?? null);
    const contactSummary = contact ?? series.contact_assigned ?? null;
    if (!contactSummary?.name) return null;
    return contactSummary.title ? `${contactSummary.name} - ${contactSummary.title}` : contactSummary.name;
  };

  const handleDropdownChange = (selectedId: string) => {
    setNotification(null);
    const idx = parseInt(selectedId, 10);
    const p = stepsInSelectedSeries[idx];
    if (p) {
      setSelectedStepNumber(getPromptStepNumber(p) ?? idx + 1);
    }
  };

  const seriesDropdownOptions = useMemo(
    () =>
      filteredSeries.map((series) => {
        const displayTitle = getSeriesDisplayTitle(series);
        const contactLabel = getSeriesContactLabel(series);
        const stepLabel = `${series.total_steps} step${series.total_steps !== 1 ? "s" : ""}`;
        const channels = getSeriesChannelSummary(series);
        const label = contactLabel
          ? `${displayTitle} · ${contactLabel} · ${stepLabel} · ${channels}`
          : `${displayTitle} · ${stepLabel} · ${channels}`;
        return { value: series.id, label };
      }),
    [filteredSeries, localPossibleContacts]
  );

  const compactSeriesDisplayTitles = useMemo(() => {
    const seriesNumbers = new Map(
      [...filteredSeries]
        .sort((a, b) => {
          const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
          return aTime - bTime;
        })
        .map((series, index) => [series.id, index + 1] as const)
    );

    return new Map(filteredSeries.map((series, index) => {
      const title = series.title?.trim();
      const label = title || (series.kind === SocialPromptSeriesKind.SEQUENCE
        ? `Sequence ${seriesNumbers.get(series.id) ?? index + 1}`
        : formatSeriesKind(series.kind));
      return [series.id, label] as const;
    }));
  }, [filteredSeries]);

  const compactSeriesDropdownOptions = useMemo(() => {
    return filteredSeries.map((series, index) => {
      const baseLabel = compactSeriesDisplayTitles.get(series.id) ?? `Sequence ${index + 1}`;
      const createdLabel = formatSeriesCreatedAt(series.created_at);
      const label = createdLabel
        ? `${baseLabel} · Created: ${createdLabel}`
        : baseLabel;
      return { value: series.id, label };
    });
  }, [filteredSeries, compactSeriesDisplayTitles]);

  const defaultSeriesDropdownIndex = useMemo(() => {
    if (!selectedSeriesId || seriesDropdownOptions.length === 0) return 0;
    const ix = seriesDropdownOptions.findIndex((option) => option.value === selectedSeriesId);
    return ix === -1 ? 0 : ix;
  }, [selectedSeriesId, seriesDropdownOptions]);

  const handleSeriesDropdownChange = (seriesId: string) => {
    if (seriesId) {
      handleSeriesClick(seriesId);
    }
  };

  const handleRequestMorePrompts = () => {
    if (!anchorId || !category) {
      setNotification({type: 'error', message: "Unable to request prompts: missing insight information"});
      setTimeout(() => setNotification(null), 5000);
      return;
    }
    setIsRequesting(true);
    setNotification(null);

    const content_plan = cadenceSteps.map((s) =>
      createCadenceStep(s.content_type, s.objective)
    );
    const title = sequenceTitle.trim() || null;

    let requestData: IInsightsItemSPRequest | IAccountInsightSPRequest | IIndustryInsightSPRequest | IContactIntelligenceSPRequest | IAssetSPRequest;

    if (category === "INSIGHTS_ITEM" && origin) {
      requestData = { title, voice_key: selectedVoice || null, context: additionalContext.trim() || null, content_plan, contact_id: selectedContact?.id || null, account_id: accountId, target_type: "INSIGHTS_ITEM", insight_item_id: origin.id };
    } else if (insightId == null) {
      setIsRequesting(false);
      setNotification({type: "error", message: "The insight anchor is unavailable."});
      return;
    } else if (category === INSIGHT_CATEGORY.ACCOUNT) {
      requestData = {
        title,
        voice_key: selectedVoice || null,
        context: additionalContext.trim() || null,
        content_plan,
        contact_id: selectedContact?.id || null,
        target_type: "ACCOUNT_INSIGHT",
        account_insight_id: insightId
      };
    } else if (category === INSIGHT_CATEGORY.INDUSTRY) {
      requestData = {
        title,
        voice_key: selectedVoice || null,
        context: additionalContext.trim() || null,
        content_plan,
        contact_id: selectedContact?.id || null,
        target_type: "INDUSTRY_INSIGHT",
        industry_insight_id: insightId,
        account_id: accountId
      };
    } else if (category === INSIGHT_CATEGORY.ASSET) {
      requestData = {
        title,
        voice_key: selectedVoice || null,
        context: additionalContext.trim() || null,
        content_plan,
        target_type: "ASSET_INSIGHT",
        asset_id: insightId
      };
    } else {
      requestData = {
        title,
        voice_key: selectedVoice || null,
        context: additionalContext.trim() || null,
        content_plan,
        target_type: "CONTACT_INTELLIGENCE",
        contact_id: insightId
      };
    }

    requestSocialPrompt(
      requestData,
      (response) => {
        if (presentation === "workspace") {
          setRequestSubmitted(true);
        } else {
          setNotification({type: 'success', message: "Request submitted successfully! You will receive a notification when the prompts are ready."});
          setTimeout(() => setNotification(null), 3000);
        }
        resetDefaults();
      },
      (error) => {
        setNotification({type: 'error', message: "Failed to submit request. Please try again."});
        setTimeout(() => setNotification(null), 5000);
        setIsRequesting(false);
      }
    );
  };

  const resetDefaults = () => {
    const targetedContact =
      category === "CONTACT_INTELLIGENCE" && insightId != null
        ? localPossibleContacts?.find((contact) => contact.id === insightId) ?? null
        : null;
    setSelectedContact(targetedContact);
    setCadenceSteps([
      {
        id: generateCadenceStepId(),
        content_type: SOCIAL_CONTENT_TYPE.EMAIL,
        objective: "",
      },
    ]);
    setAdditionalContext("");
    setSequenceTitle("");
    setIsRequesting(false);
    setSelectedVoice("");
    setSelectedTemplateId("");
    setIsSaveTemplateOpen(false);
    setSaveTemplateTitle("");
    setSaveTemplateError(null);
    setDuplicateTemplateId(null);
  }

  const handleApplyTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    setNotification(null);
    setIsLoadingTemplate(true);
    getSocialPromptCadenceTemplate(templateId)
      .then((template) => {
        setSequenceTitle(template.title ?? "");
        setAdditionalContext(template.context ?? "");
        const mappedSteps: CadenceStepUi[] =
          template.steps.length > 0
            ? template.steps.map((step) => ({
                id: generateCadenceStepId(),
                content_type: step.content_type,
                objective: step.objective ?? "",
              }))
            : [createDefaultCadenceStep()];
        setCadenceSteps(mappedSteps);

        const voiceKey = template.voice_key ?? "";
        setSelectedVoice(voiceKey);
        const voiceUnavailable =
          !!voiceKey && !availableVoices.some((voice) => voice.key === voiceKey);
        const hasSoloChannel = mappedSteps.some((step) =>
          step.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_POST ||
          step.content_type === SOCIAL_CONTENT_TYPE.BLOG
        );
        const channelUnsupported =
          category === "CONTACT_INTELLIGENCE" && hasSoloChannel;
        if (voiceUnavailable || channelUnsupported) {
          const reasons: string[] = [];
          if (voiceUnavailable) reasons.push("its saved voice isn't currently available");
          if (channelUnsupported)
            reasons.push("a LinkedIn post or blog step isn't supported for this contact");
          setNotification({
            type: "info",
            message: `Template loaded, but ${reasons.join(" and ")}. Please review before requesting.`,
          });
        }
      })
      .catch((error) => {
        setNotification({
          type: "error",
          message: getApiErrorMessage(error, "Failed to load the selected template."),
        });
        setTimeout(() => setNotification(null), 5000);
      })
      .finally(() => {
        setIsLoadingTemplate(false);
      });
  };

  const openSaveTemplateDialog = () => {
    setSaveTemplateTitle(sequenceTitle.trim());
    setSaveTemplateError(null);
    setDuplicateTemplateId(null);
    setIsSaveTemplateOpen(true);
  };

  const closeSaveTemplateDialog = () => {
    setIsSaveTemplateOpen(false);
    setSaveTemplateError(null);
    setDuplicateTemplateId(null);
  };

  const buildTemplateSteps = () =>
    cadenceSteps.map((step) => ({
      content_type: step.content_type,
      objective: step.objective.trim() ? step.objective.trim() : null,
    }));

  const handleSaveTemplate = () => {
    const trimmedTitle = saveTemplateTitle.trim();
    if (!trimmedTitle) {
      setSaveTemplateError("Please enter a title for this template.");
      return;
    }
    if (trimmedTitle.length > 255) {
      setSaveTemplateError("Title must be 255 characters or fewer.");
      return;
    }
    if (cadenceSteps.length < 1 || cadenceSteps.length > MAX_CADENCE_STEPS) {
      setSaveTemplateError(`A template must have between 1 and ${MAX_CADENCE_STEPS} steps.`);
      return;
    }

    setIsSavingTemplate(true);
    setSaveTemplateError(null);

    const payload = {
      title: trimmedTitle,
      voice_key: selectedVoice || null,
      context: additionalContext.trim() || null,
      steps: buildTemplateSteps(),
    };

    createSocialPromptCadenceTemplate(payload)
      .then(() => {
        loadCadenceTemplates();
        closeSaveTemplateDialog();
        setNotification({ type: "success", message: "Template saved." });
        setTimeout(() => setNotification(null), 3000);
      })
      .catch((error) => {
        if (getApiErrorStatus(error) === 409) {
          const existing = templates.find(
            (t) => t.title.trim().toLowerCase() === trimmedTitle.toLowerCase()
          );
          setDuplicateTemplateId(existing?.id ?? null);
          setSaveTemplateError(
            "You already have a template with this title. Rename it, or update the existing one."
          );
        } else {
          setSaveTemplateError(getApiErrorMessage(error, "Failed to save template."));
        }
      })
      .finally(() => {
        setIsSavingTemplate(false);
      });
  };

  const handleUpdateExistingTemplate = () => {
    if (!duplicateTemplateId) return;
    setIsSavingTemplate(true);
    setSaveTemplateError(null);

    updateSocialPromptCadenceTemplate(duplicateTemplateId, {
      title: saveTemplateTitle.trim(),
      voice_key: selectedVoice || null,
      context: additionalContext.trim() || null,
      steps: buildTemplateSteps(),
    })
      .then(() => {
        loadCadenceTemplates();
        closeSaveTemplateDialog();
        setNotification({ type: "success", message: "Template updated." });
        setTimeout(() => setNotification(null), 3000);
      })
      .catch((error) => {
        setSaveTemplateError(getApiErrorMessage(error, "Failed to update template."));
      })
      .finally(() => {
        setIsSavingTemplate(false);
      });
  };

  const handlePageChange = (page: 'prompts' | 'request') => {
    setShowEntryChoices(false);
    setRequestSubmitted(false);
    setCurrentPage(page);
    setRequestWizardStep(1);
    resetDefaults();
  };

  const handleWorkspaceBack = () => {
    onClose();
  };

  const getAuthorInitials = (authorName: string) => {
    return authorName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const renderAuthorIndicator = (prompt: ISocialPrompt) => {
    const authorName = getAuthorForPrompt(prompt);
    const initials = getAuthorInitials(authorName);
    
    return (
      <div 
        className="socialPromptModal_authorIndicator"
        onMouseEnter={(e) => showTooltip(e, `Author: ${authorName}`)}
        onMouseLeave={hideTooltip}
      >
        {initials}
      </div>
    );
  };

  const renderContactSection = (
    cadenceOrPromptType: CadenceStepUi[] | string,
    hint: string,
  ) => {
    if (!localPossibleContacts?.length) return null;

    const summaryLabel = selectedContact
      ? [selectedContact.name, selectedContact.title].filter(Boolean).join(" · ")
      : "Choose a recipient";
    const contactsForChannel = getContactsOrderedForChannel(cadenceOrPromptType);
    const visibleContactIndex = Math.min(
      contactCarouselIndex,
      Math.max(contactsForChannel.length - 1, 0)
    );

    if (isCompactOutreach) {
      return (
        <details
          className="socialPromptModal_contactDisclosure"
          defaultOpen={!selectedContact}
          ref={contactDisclosureRef}
        >
          <summary className="socialPromptModal_contactDisclosureSummary">
            <span className="socialPromptModal_contactDisclosureIcon" aria-hidden>
              <i className="fa-regular fa-user" />
            </span>
            <span className="socialPromptModal_contactDisclosureText">
              <span className="socialPromptModal_contactDisclosureLabel">Recipient</span>
              <span className="socialPromptModal_contactDisclosureValue">{summaryLabel}</span>
            </span>
            <span className="socialPromptModal_contactDisclosureAction">
              {selectedContact ? "Change" : "Select"}
            </span>
            <i className="fa-solid fa-chevron-down socialPromptModal_contactDisclosureChevron" aria-hidden />
          </summary>
          <div className="socialPromptModal_contactDisclosureBody">
            <p className="socialPromptModal_contactHint">{hint}</p>
            <div className="socialPromptModal_contactCarousel">
              <button
                type="button"
                className="socialPromptModal_contactPager"
                aria-label="Previous recipient"
                disabled={visibleContactIndex === 0}
                onClick={() => scrollContactCarouselTo(contactsForChannel, visibleContactIndex - 1)}
              >
                <i className="fa-solid fa-chevron-left" aria-hidden />
              </button>
              <div
                className="socialPromptModal_contactStrip"
                ref={contactListRef}
                data-testid="social-prompt-contact-strip"
                data-tour="social-prompt-contacts"
              >
                {contactsForChannel.map((contact) => renderContactCard(contact, cadenceOrPromptType))}
              </div>
              <button
                type="button"
                className="socialPromptModal_contactPager"
                aria-label="Next recipient"
                disabled={visibleContactIndex === contactsForChannel.length - 1}
                onClick={() => scrollContactCarouselTo(contactsForChannel, visibleContactIndex + 1)}
              >
                <i className="fa-solid fa-chevron-right" aria-hidden />
              </button>
            </div>
          </div>
        </details>
      );
    }

    return (
      <section className="socialPromptModal_contactSection">
        <div className="socialPromptModal_contactSectionHead">
          <span className="socialPromptModal_contactSummaryTitle">Send to</span>
          <span className="socialPromptModal_contactSummaryValue">{summaryLabel}</span>
        </div>
        <p className="socialPromptModal_contactHint">{hint}</p>
        <div
          className="socialPromptModal_contactStrip"
          ref={contactListRef}
          data-testid="social-prompt-contact-strip"
          data-tour="social-prompt-contacts"
        >
          {getContactsOrderedForChannel(cadenceOrPromptType).map((contact) =>
            renderContactCard(contact, cadenceOrPromptType)
          )}
        </div>
      </section>
    );
  };

  const renderPromptsContextBar = (compact = false) => {
    const dropdownOptions = compact
      ? compactSeriesDropdownOptions
      : seriesDropdownOptions;
    const showSeriesDropdown = compact
      ? dropdownOptions.length > 1
      : dropdownOptions.length > 0;
    const selectedCompactSeriesLabel =
      compactSeriesDropdownOptions.find((option) => option.value === selectedSeriesId)?.label ??
      compactSeriesDropdownOptions[0]?.label ??
      "Choose a sequence";
    const selectedJourneyStepIndex = Math.max(
      0,
      stepsInSelectedSeries.findIndex((prompt) => prompt.id === selectedPrompt?.id)
    );

    if (compact) {
      return (
        <section className="socialPromptModal_journey" aria-label="Outreach sequence">
          <div className="socialPromptModal_journeyControls">
            {showSeriesDropdown ? (
              <div className="socialPromptModal_sequenceChooser">
                <button
                  type="button"
                  className="socialPromptModal_sequenceChooserTrigger"
                  aria-expanded={isSequenceChooserOpen}
                  aria-controls="social-prompt-sequence-options"
                  onClick={() => setIsSequenceChooserOpen((open) => !open)}
                >
                  <span className="socialPromptModal_sequenceChooserSummary">
                    <span className="socialPromptModal_sequenceChooserLabel">
                      <i className="fa-regular fa-diagram-project" aria-hidden />
                      Outreach sequence
                    </span>
                    <span className="socialPromptModal_sequenceChooserValueRow">
                      <span className="socialPromptModal_sequenceChooserValue">
                        {selectedCompactSeriesLabel}
                      </span>
                      <span className="socialPromptModal_sequenceChooserAction">
                        <i className="fa-solid fa-chevron-down" aria-hidden />
                      </span>
                    </span>
                  </span>
                </button>

                {isSequenceChooserOpen ? (
                  <div
                    id="social-prompt-sequence-options"
                    className="socialPromptModal_sequenceChooserMenu"
                    role="listbox"
                    aria-label="Choose an outreach sequence"
                  >
                    <p className="socialPromptModal_sequenceChooserMenuTitle">Choose a sequence</p>
                    {compactSeriesDropdownOptions.map((option) => {
                      const isCurrent = option.value === selectedSeriesId;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className="socialPromptModal_sequenceChooserOption"
                          role="option"
                          aria-selected={isCurrent}
                          onClick={() => {
                            handleSeriesClick(option.value);
                            setIsSequenceChooserOpen(false);
                          }}
                        >
                          <span>{option.label}</span>
                          {isCurrent ? <span className="socialPromptModal_sequenceChooserCurrent">Current</span> : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="socialPromptModal_sequenceChooser socialPromptModal_sequenceChooser--single">
                <div className="socialPromptModal_sequenceChooserTrigger">
                  <span className="socialPromptModal_sequenceChooserSummary">
                    <span className="socialPromptModal_sequenceChooserLabel">
                      <i className="fa-regular fa-diagram-project" aria-hidden />
                      Outreach sequence
                    </span>
                    <span className="socialPromptModal_sequenceChooserValueRow">
                      <span className="socialPromptModal_sequenceChooserValue">
                        {selectedCompactSeriesLabel}
                      </span>
                    </span>
                  </span>
                </div>
              </div>
            )}
            <div className="socialPromptModal_journeySequenceActions">
              {canAdministerPrompts ? (
                <button
                  type="button"
                  className="socialPromptModal_journeyDeleteSequence"
                  onClick={handleDelete}
                >
                  <i className="fa-solid fa-trash" aria-hidden />
                  Delete sequence
                </button>
              ) : null}
            </div>
          </div>

          {stepsInSelectedSeries.length > 0 ? (
            <div className="socialPromptModal_journeyStepCarousel">
              <button
                type="button"
                className="socialPromptModal_journeyStepPager"
                aria-label="Previous sequence step"
                disabled={selectedJourneyStepIndex === 0}
                onClick={() => handleStepClick(stepsInSelectedSeries[selectedJourneyStepIndex - 1])}
              >
                <i className="fa-solid fa-chevron-left" aria-hidden />
              </button>
              <nav
                ref={journeyStepsRef}
                className="socialPromptModal_journeySteps"
                aria-label="Sequence steps"
              >
                {stepsInSelectedSeries.map((prompt, index) => {
                const isActive = selectedPrompt?.id === prompt.id;
                const isCompleted =
                  prompt.interaction_status.insight_status === INSIGHT_STATUS.COMPLETED;
                const stepNumber = getPromptStepNumber(prompt) ?? index + 1;
                const contentLabel = prompt.content_type
                  ? formatEnumValue(prompt.content_type)
                  : "Prompt";

                return (
                  <Fragment key={prompt.id}>
                    {index > 0 ? (
                      <span className="socialPromptModal_journeyArrow" aria-hidden>
                        <i className="fa-solid fa-arrow-right" />
                      </span>
                    ) : null}
                    <button
                      type="button"
                      className={[
                        "socialPromptModal_journeyStep",
                        isActive ? "socialPromptModal_journeyStep--active" : "",
                        isCompleted ? "socialPromptModal_journeyStep--done" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => handleStepClick(prompt)}
                      aria-current={isActive ? "step" : undefined}
                      aria-label={`Step ${stepNumber}: ${contentLabel}${isCompleted ? ", completed" : ""}`}
                    >
                      <span className="socialPromptModal_journeyStepNumber">
                        {isCompleted ? (
                          <i className="fa-solid fa-check" aria-hidden />
                        ) : (
                          stepNumber
                        )}
                      </span>
                      <span className="socialPromptModal_journeyStepLabel">
                        <i className={getContentTypeIconClass(prompt.content_type)} aria-hidden />
                        {contentLabel}
                      </span>
                    </button>
                  </Fragment>
                );
                })}
              </nav>
              <button
                type="button"
                className="socialPromptModal_journeyStepPager"
                aria-label="Next sequence step"
                disabled={selectedJourneyStepIndex === stepsInSelectedSeries.length - 1}
                onClick={() => handleStepClick(stepsInSelectedSeries[selectedJourneyStepIndex + 1])}
              >
                <i className="fa-solid fa-chevron-right" aria-hidden />
              </button>
            </div>
          ) : null}
        </section>
      );
    }

    return (
      <div className="socialPromptModal_contextBar">
        <div className="socialPromptModal_contextFields">
          {showSeriesDropdown && (
            <div className="socialPromptModal_field socialPromptModal_field--grow">
              <label className="socialPromptModal_fieldLabel" htmlFor="social-prompt-series-filter">
                Sequence
              </label>
              <div className="socialPromptModal_glassDropdown" id="social-prompt-series-filter">
                <CustomDropdown
                  key={selectedSeriesId ?? "series"}
                  options={dropdownOptions}
                  onChange={handleSeriesDropdownChange}
                  defaultOptionIndex={defaultSeriesDropdownIndex}
                  portaledMenu
                  testId="series-dropdown-select"
                />
              </div>
            </div>
          )}

          {stepsInSelectedSeries.length > 0 && (
            <div className="socialPromptModal_field">
              <label className="socialPromptModal_fieldLabel" htmlFor="social-prompt-step-filter">
                Step
              </label>
              <div className="socialPromptModal_glassDropdown" id="social-prompt-step-filter">
                <CustomDropdown
                  key={selectedSeriesId ?? "step"}
                  options={stepDropdownOptions}
                  onChange={handleDropdownChange}
                  defaultOptionIndex={defaultDropDownLabelIndex}
                  portaledMenu
                  testId="dropdown-select"
                />
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          className="socialPromptModal_requestMoreLink"
          onClick={() => handlePageChange("request")}
        >
          <i className="fa-solid fa-plus" aria-hidden />
          Request More
        </button>
      </div>
    );
  };

  const renderMarkCompleteButton = (className = "socialPromptModal_headerCompleteBtn") => {
    if (currentPage !== "prompts" || !selectedPrompt) return null;

    return (
      <button
        type="button"
        className={`${className} ${isChecked ? "socialPromptModal_headerCompleteBtn--completed" : ""}`}
        onClick={!isChecked ? handleCheckboxChange : undefined}
        aria-label={isChecked ? "Prompt completed" : "Mark prompt as complete"}
        disabled={isChecked}
        title={isChecked ? "Prompt completed" : "Mark this prompt as completed"}
      >
        {isChecked ? (
          <>
            <i className="fa-solid fa-circle-check" aria-hidden />
            Done
          </>
        ) : (
          <>
            <i className="fa-regular fa-circle-check" aria-hidden />
            Mark done
          </>
        )}
      </button>
    );
  };

  const renderHeaderTrailingActions = () => {
    if (presentation === "workspace") return null;
    return renderMarkCompleteButton();
  };

  const renderPromptsPage = () => {
    const seriesContactLabel = selectedSeries ? getSeriesContactLabel(selectedSeries) : null;

    return (
      <div className="socialPromptModal_promptsPage socialPromptModal_promptsPage--simplified">
        {renderPromptsContextBar()}
        {renderSeriesRevision()}

        {seriesContactLabel ? (
          <p className="socialPromptModal_seriesContactMeta">For {seriesContactLabel}</p>
        ) : null}

        <div className="socialPromptModal_mainContent socialPromptModal_mainContent--single">
          <div className="socialPromptModal_contentContainer">
            <div className="socialPromptModal_composeCard">
              {renderSubjectField()}

              <div className="socialPromptModal_composeField socialPromptModal_composeField--message">
                <SocialPromptMessageEditor
                  id={selectedPrompt?.id != null ? String(selectedPrompt.id) + "message" : "message"}
                  message={message}
                  messageFormat={selectedPrompt?.message_format ?? MESSAGE_FORMAT.TEXT}
                  onChange={setMessage}
                  textareaClassName="socialPromptModal_messageTextArea"
                  ariaLabel={getPromptMessageFieldLabel(
                    selectedPrompt?.content_type,
                    selectedPrompt?.message_format ?? MESSAGE_FORMAT.TEXT
                  )}
                  renderToolbar={(toolbar) => (
                    <div className="socialPromptModal_composeFieldLabelRow">
                      <label
                        className="socialPromptModal_composeFieldLabel"
                        htmlFor={
                          selectedPrompt ? selectedPrompt.id + "message" : "message"
                        }
                      >
                        {getPromptMessageFieldLabel(
                          selectedPrompt?.content_type,
                          selectedPrompt?.message_format ?? MESSAGE_FORMAT.TEXT
                        )}
                      </label>
                      <div className="socialPromptModal_composeFieldLabelActions">
                        {toolbar}
                        {currentAuthorLabel ? (
                          <span className="socialPromptModal_authorTag">
                            <i className="fa-regular fa-user-pen" aria-hidden />
                            {currentAuthorLabel}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>

            {renderContactSection(
              selectedPrompt?.content_type || "",
              "Pick who should receive this outreach. Contacts that don't match the channel are grayed out.",
            )}

            {renderNotification()}
            <div className="socialPromptModal_footerButtonRow">
              {selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.EMAIL && (
                <button
                  type="button"
                  className="socialPromptModal_footerButton socialPromptModal_footerButton--primary"
                  onClick={handleSend}
                >
                  <i className="fa-solid fa-paper-plane" aria-hidden />
                  <span className="socialPromptModal_footerButtonText">
                    {getSendButtonLabel(selectedContact)}
                  </span>
                </button>
              )}
              {(selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_DM ||
                selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_POST) && (
                <button
                  type="button"
                  className="socialPromptModal_footerButton socialPromptModal_footerButton--primary"
                  onClick={() =>
                    handleOpenLinkedin(
                      selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_DM
                    )
                  }
                >
                  <i className="fa-brands fa-linkedin" aria-hidden />
                  <span className="socialPromptModal_footerButtonText">LinkedIn</span>
                </button>
              )}
              {selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.BLOG && (
                <button
                  type="button"
                  className="socialPromptModal_footerButton socialPromptModal_footerButton--primary"
                  onClick={() => {
                    void handleCopyMessage();
                  }}
                >
                  <i className="fa-solid fa-pen-nib" aria-hidden />
                  <span className="socialPromptModal_footerButtonText">Copy Blog</span>
                </button>
              )}
              {(selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_DM ||
                selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_POST) && (
                <button
                  type="button"
                  className="socialPromptModal_footerButton socialPromptModal_footerButton--ghost"
                  onClick={() => {
                    void handleCopyMessage();
                  }}
                >
                  <i className="fa-regular fa-copy" aria-hidden />
                  <span className="socialPromptModal_footerButtonText">Copy</span>
                </button>
              )}
              {allowPromptSave && (
                <button
                  type="button"
                  className="socialPromptModal_footerButton socialPromptModal_footerButton--ghost socialPromptModal_footerButton--save"
                  onClick={handleSave}
                >
                  <i className="fa-solid fa-floppy-disk" aria-hidden />
                  <span className="socialPromptModal_footerButtonText">Save</span>
                </button>
              )}
              {(isSalesLeader || isClientAdmin || adminAccess) && (
                <button
                  type="button"
                  className="socialPromptModal_footerButton socialPromptModal_footerButton--danger"
                  onClick={handleDelete}
                >
                  <i className="fa-solid fa-trash" aria-hidden />
                  <span className="socialPromptModal_footerButtonText">Delete sequence</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNotification = () => {
    if (!notification) return null;

    return (
      <div
        className={`socialPromptModal_notice socialPromptModal_notice--${notification.type}`}
        role="status"
      >
        <span
          className={`fa-solid ${
            notification.type === "success"
              ? "fa-circle-check"
              : notification.type === "error"
                ? "fa-circle-exclamation"
                : "fa-circle-info"
          }`}
          aria-hidden
        />
        <span>{notification.message}</span>
      </div>
    );
  };

  const renderRequestSubmitButton = (
    options: { className?: string; label?: string } = {}
  ) => (
    <button
      type="button"
      className={[
        "socialPromptModal_footerButton",
        "socialPromptModal_footerButton--primary",
        options.className,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleRequestMorePrompts}
      disabled={isRequesting}
    >
      {isRequesting ? (
        <>
          <i className="fa-solid fa-spinner fa-spin" aria-hidden />
          <span className="socialPromptModal_footerButtonText">Submitting…</span>
        </>
      ) : (
        <>
          <i className="fa-solid fa-paper-plane" aria-hidden />
          <span className="socialPromptModal_footerButtonText">
            {options.label ?? "Request"}
          </span>
        </>
      )}
    </button>
  );

  const renderRequestPage = () => {
    const hasExistingPrompts = prompts.length > 0;

    const renderSectionSummary = (title: string, hint: string) => (
      <>
        <span className="socialPromptModal_sectionSummaryMain">
          <span>{title}</span>
          <span className="socialPromptModal_sectionSummaryHint">{hint}</span>
        </span>
        <i className="fa-solid fa-chevron-down socialPromptModal_sectionChevron" aria-hidden />
      </>
    );

    return (
      <div className="socialPromptModal_promptsPage socialPromptModal_promptsPage--simplified socialPromptModal_requestPage">
        {hasExistingPrompts ? (
          <div className="socialPromptModal_requestNavBar">
            <button
              type="button"
              className="socialPromptModal_requestBackButton"
              onClick={() => handlePageChange("prompts")}
            >
              <i className="fa-solid fa-arrow-left" aria-hidden />
              Back to Prompts
            </button>
          </div>
        ) : null}

        <div className="socialPromptModal_mainContent socialPromptModal_mainContent--single socialPromptModal_mainContent--request">
          <div className="socialPromptModal_contentContainer socialPromptModal_contentContainer--request">
            <header className="socialPromptModal_requestPageIntro">
              <h2 className="socialPromptModal_requestPageTitle">
                {hasExistingPrompts ? "Request More Prompts" : "Request Outreach Prompts"}
              </h2>
              <p className="socialPromptModal_requestPageLead">
                {hasExistingPrompts
                  ? cadencesEnabled
                    ? "Add another outreach sequence for this insight."
                    : "Add another outreach prompt for this insight."
                  : cadencesEnabled
                    ? "Choose your channels and cadence — we'll generate a tailored sequence."
                    : "Choose a channel — we'll generate a tailored prompt."}
              </p>
            </header>

            <details className="socialPromptModal_sectionDetails" open>
              <summary className="socialPromptModal_sectionSummary">
                {renderSectionSummary(
                  cadencesEnabled ? "Build your sequence" : "Build your prompt",
                  cadencesEnabled ? "Channels and steps" : "Channel and details"
                )}
              </summary>
              <div className="socialPromptModal_sectionDetailsBody">
                {cadencesEnabled && templates.length > 0 && (
                  <div className="socialPromptModal_textBoxSection">
                    <div className="socialPromptModal_sectionTitleText">Saved template</div>
                    <div className="socialPromptModal_glassDropdown">
                      <CustomDropdown
                        key={`template-picker-${selectedTemplateId}`}
                        options={[
                          { value: "", label: "Start from a saved template…" },
                          ...templates.map((template) => ({
                            value: template.id,
                            label: `${template.title} (${template.step_count} step${
                              template.step_count !== 1 ? "s" : ""
                            })`,
                          })),
                        ]}
                        onChange={handleApplyTemplate}
                        defaultOptionIndex={Math.max(
                          0,
                          templates.findIndex((t) => t.id === selectedTemplateId) + 1
                        )}
                        portaledMenu
                        testId="template-dropdown-select"
                      />
                    </div>
                    {isLoadingTemplate && (
                      <div className="socialPromptModal_voiceDescription">
                        <i className="fa-solid fa-spinner fa-spin" aria-hidden /> Loading template…
                      </div>
                    )}
                  </div>
                )}
                <div className="socialPromptModal_textBoxSection">
                  <div className="socialPromptModal_sectionTitleText">Title</div>
                  <input
                    className="socialPromptModal_sequenceTitleInput"
                    value={sequenceTitle}
                    onChange={(e) => setSequenceTitle(e.target.value)}
                    placeholder={
                      cadencesEnabled
                        ? "Name this sequence for easier selection later"
                        : "Name this prompt for easier selection later"
                    }
                    maxLength={255}
                  />
                </div>
                <div className="socialPromptModal_textBoxSection">
                  <div className="socialPromptModal_sectionTitleText">
                    {cadencesEnabled ? "Cadence (1–5 steps)" : "Channel"}
                  </div>
                  <div className="socialPromptModal_cadenceDescription">
                    {cadenceBuilderHint}
                  </div>
                  <CadenceStepEditor
                    steps={cadenceSteps}
                    onChange={setCadenceSteps}
                    excludeSoloChannels={category === "CONTACT_INTELLIGENCE"}
                    maxSteps={cadenceMaxSteps}
                    addStepRowActions={
                      cadencesEnabled ? (
                        <button
                          type="button"
                          className="socialPromptModal_footerButton socialPromptModal_footerButton--ghost"
                          onClick={openSaveTemplateDialog}
                        >
                          <i className="fa-regular fa-bookmark" aria-hidden />
                          <span className="socialPromptModal_footerButtonText">Save as template</span>
                        </button>
                      ) : undefined
                    }
                  />
                </div>
              </div>
            </details>

            <details className="socialPromptModal_sectionDetails">
              <summary className="socialPromptModal_sectionSummary">
                {renderSectionSummary("Fine-tune (optional)", "Voice and extra context")}
              </summary>
              <div className="socialPromptModal_sectionDetailsBody">
                <div className="socialPromptModal_textBoxSection">
                  <div className="socialPromptModal_sectionTitleText">Voice style</div>
                  <div className="socialPromptModal_glassDropdown">
                    <CustomDropdown
                      key={`voice-${selectedVoice}`}
                      options={[
                        { value: "", label: "Use your default voice" },
                        ...availableVoices.map((voice) => ({
                          value: voice.key,
                          label: voice.label,
                        })),
                      ]}
                      onChange={(value) => setSelectedVoice(value)}
                      defaultOptionIndex={Math.max(
                        0,
                        availableVoices.findIndex((voice) => voice.key === selectedVoice) + 1
                      )}
                      portaledMenu
                      testId="voice-dropdown-select"
                    />
                  </div>
                  {selectedVoice &&
                    availableVoices.find((voice) => voice.key === selectedVoice)?.description && (
                      <div className="socialPromptModal_voiceDescription">
                        {availableVoices.find((voice) => voice.key === selectedVoice)?.description}
                      </div>
                    )}
                </div>

                <div className="socialPromptModal_textBoxSection">
                  <div className="socialPromptModal_sectionTitleText">Extra context</div>
                  <textarea
                    className="socialPromptModal_messageTextArea socialPromptModal_contextTextArea"
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    placeholder="Anything specific for this request — tone, angle, or details for the AI."
                    rows={3}
                  />
                </div>
              </div>
            </details>

            {renderContactSection(
              cadenceSteps,
              "Helps tailor generated prompts to a specific contact.",
            )}

            {renderNotification()}
          </div>

          <div className="socialPromptModal_requestFooter">
            <div className="socialPromptModal_footerButtonRow socialPromptModal_footerButtonRow--request">
              {renderRequestSubmitButton()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const headerSubtitle =
    currentPage === "request"
      ? prompts.length === 0
        ? cadencesEnabled
          ? "Request a new outreach sequence for this insight"
          : "Request a new outreach prompt for this insight"
        : cadencesEnabled
          ? "Add another sequence to this insight"
          : "Add another prompt to this insight"
      : "Compose, personalize, and send your outreach";

  const workspaceInsightTitle =
    insightContext?.title?.trim() || "Outreach";

  const renderWorkspaceHeadingContent = () => {
    const synopsis = insightContext?.synopsis?.trim() || null;
    const notes = insightContext?.notes ?? [];
    const sourceUrl = insightContext?.sourceUrl?.trim() || null;
    const hasNotes = notes.length > 0;
    const synopsisForDisplay =
      synopsis && synopsis !== workspaceInsightTitle ? synopsis : null;

    if (insightContextLoading) {
      return (
        <div className="outreachWorkspace_insightContextLoading">
          <div className="outreachWorkspace_insightContextSkeletonTitle" />
          <div className="outreachWorkspace_insightContextSkeletonSynopsis" />
        </div>
      );
    }

    if (isCompactOutreach) {
      return (
        <>
          <p className="outreachWorkspace_kicker">{getSocialPromptKicker(category)}</p>
          <h1 className="outreachWorkspace_title">
            {sourceUrl ? (
              <a
                href={sourceUrl}
                className="outreachWorkspace_titleLink"
                target="_blank"
                rel="noopener noreferrer"
              >
                {workspaceInsightTitle}
              </a>
            ) : (
              workspaceInsightTitle
            )}
          </h1>
          <div className="outreachWorkspace_compactHeadingActions">
            {synopsisForDisplay || hasNotes ? (
              <details className="outreachWorkspace_contextDisclosure">
                <summary className="outreachWorkspace_contextDisclosureSummary">
                  <i className="fa-regular fa-lightbulb" aria-hidden />
                  Why this outreach
                </summary>
                <div className="outreachWorkspace_contextDisclosureBody">
                  {synopsisForDisplay ? (
                    <p className="outreachWorkspace_insightContextSynopsis">
                      {synopsisForDisplay}
                    </p>
                  ) : null}
                  {hasNotes ? (
                    <ul className="outreachWorkspace_insightContextNotesList">
                      {notes.map((note, index) => (
                        <li key={`${index}-${note.slice(0, 24)}`}>{note}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </details>
            ) : <span />}
            {promptViewReady && currentPage === "prompts" ? (
              <button
                type="button"
                className="outreachWorkspace_newSequenceBtn"
                onClick={() => handlePageChange("request")}
              >
                <i className="fa-solid fa-plus" aria-hidden />
                New Sequence
              </button>
            ) : null}
          </div>
        </>
      );
    }

    return (
      <>
        <p className="outreachWorkspace_kicker">{getSocialPromptKicker(category)}</p>
        <h1 className="outreachWorkspace_title">
          {sourceUrl ? (
            <a
              href={sourceUrl}
              className="outreachWorkspace_titleLink"
              target="_blank"
              rel="noopener noreferrer"
            >
              {workspaceInsightTitle}
            </a>
          ) : (
            workspaceInsightTitle
          )}
        </h1>
        {synopsisForDisplay ? (
          <p className="outreachWorkspace_subtitle outreachWorkspace_insightContextSynopsis">
            {synopsisForDisplay}
          </p>
        ) : null}
        {hasNotes ? (
          <details className="outreachWorkspace_insightContextNotes">
            <summary className="outreachWorkspace_insightContextNotesSummary">
              Notes
              <span className="outreachWorkspace_insightContextNotesCount">
                {notes.length}
              </span>
            </summary>
            <ul className="outreachWorkspace_insightContextNotesList">
              {notes.map((note, index) => (
                <li key={`${index}-${note.slice(0, 24)}`}>{note}</li>
              ))}
            </ul>
          </details>
        ) : null}
      </>
    );
  };

  const handleStepClick = (prompt: ISocialPrompt) => {
    setNotification(null);
    setSelectedStepNumber(getPromptStepNumber(prompt) ?? 1);
  };

  const renderComposeFields = () => (
    <div className="socialPromptModal_composeCard">
      {renderSubjectField()}

      <div className="socialPromptModal_composeField socialPromptModal_composeField--message">
        <SocialPromptMessageEditor
          id={selectedPrompt?.id != null ? String(selectedPrompt.id) + "message" : "message"}
          message={message}
          messageFormat={selectedPrompt?.message_format ?? MESSAGE_FORMAT.TEXT}
          onChange={setMessage}
          textareaClassName="socialPromptModal_messageTextArea"
          ariaLabel={getPromptMessageFieldLabel(
            selectedPrompt?.content_type,
            selectedPrompt?.message_format ?? MESSAGE_FORMAT.TEXT
          )}
          renderToolbar={(toolbar) => (
            <div className="socialPromptModal_composeFieldLabelRow">
              <label
                className="socialPromptModal_composeFieldLabel"
                htmlFor={selectedPrompt ? selectedPrompt.id + "message" : "message"}
              >
                {getPromptMessageFieldLabel(
                  selectedPrompt?.content_type,
                  selectedPrompt?.message_format ?? MESSAGE_FORMAT.TEXT
                )}
              </label>
              <div className="socialPromptModal_composeFieldLabelActions">
                {toolbar}
                {currentAuthorLabel ? (
                  <span className="socialPromptModal_authorTag">
                    <i className="fa-regular fa-user-pen" aria-hidden />
                    {currentAuthorLabel}
                  </span>
                ) : null}
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );

  const canAdministerPrompts = isSalesLeader || isClientAdmin || adminAccess;
  const renderActionFooter = (includeAdminActions = true) => (
    <div
      className={`socialPromptModal_footerButtonRow ${
        isCompactOutreach ? "socialPromptModal_footerButtonRow--compact" : ""
      }`}
      data-tour="social-prompt-footer"
    >
      {selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.EMAIL && (
        <button
          type="button"
          className="socialPromptModal_footerButton socialPromptModal_footerButton--primary"
          onClick={handleSend}
        >
          <i className="fa-solid fa-paper-plane" aria-hidden />
          <span className="socialPromptModal_footerButtonText">
            {getSendButtonLabel(selectedContact)}
          </span>
        </button>
      )}
      {(selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_DM ||
        selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_POST) && (
        <button
          type="button"
          className="socialPromptModal_footerButton socialPromptModal_footerButton--primary"
          onClick={() =>
            handleOpenLinkedin(
              selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_DM
            )
          }
        >
          <i className="fa-brands fa-linkedin" aria-hidden />
          <span className="socialPromptModal_footerButtonText">LinkedIn</span>
        </button>
      )}
      {selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.BLOG && (
        <button
          type="button"
          className="socialPromptModal_footerButton socialPromptModal_footerButton--primary"
          onClick={() => {
            void handleCopyMessage();
          }}
        >
          <i className="fa-solid fa-pen-nib" aria-hidden />
          <span className="socialPromptModal_footerButtonText">Copy Blog</span>
        </button>
      )}
      {isCompactOutreach ? (
        <>
          {(selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_DM ||
            selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_POST) && (
            <button
              type="button"
              className="socialPromptModal_footerButton socialPromptModal_footerButton--ghost socialPromptModal_footerButton--copy"
              onClick={() => void handleCopyMessage()}
            >
              <i className="fa-regular fa-copy" aria-hidden />
              <span className="socialPromptModal_footerButtonText">Copy</span>
            </button>
          )}
          {includeAdminActions && allowPromptSave ? (
            <button
              type="button"
              className="socialPromptModal_footerButton socialPromptModal_footerButton--ghost socialPromptModal_footerButton--save"
              onClick={handleSave}
            >
              <i className="fa-solid fa-floppy-disk" aria-hidden />
              <span className="socialPromptModal_footerButtonText">Save</span>
            </button>
          ) : null}
          {renderMarkCompleteButton("socialPromptModal_footerCompleteBtn")}
        </>
      ) : (
        <>
          {(selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_DM ||
            selectedPrompt?.content_type === SOCIAL_CONTENT_TYPE.LINKEDIN_POST) && (
            <button
              type="button"
              className="socialPromptModal_footerButton socialPromptModal_footerButton--ghost socialPromptModal_footerButton--copy"
              onClick={() => void handleCopyMessage()}
            >
              <i className="fa-regular fa-copy" aria-hidden />
              <span className="socialPromptModal_footerButtonText">Copy</span>
            </button>
          )}
          {includeAdminActions && allowPromptSave && (
            <button
              type="button"
              className="socialPromptModal_footerButton socialPromptModal_footerButton--ghost socialPromptModal_footerButton--save"
              onClick={handleSave}
            >
              <i className="fa-solid fa-floppy-disk" aria-hidden />
              <span className="socialPromptModal_footerButtonText">Save</span>
            </button>
          )}
          {includeAdminActions &&
            (isSalesLeader || isClientAdmin || adminAccess) &&
            !(presentation === "workspace" && !isCompactOutreach) && (
            <button
              type="button"
              className="socialPromptModal_footerButton socialPromptModal_footerButton--danger"
              onClick={handleDelete}
            >
              <i className="fa-solid fa-trash" aria-hidden />
              <span className="socialPromptModal_footerButtonText">Delete sequence</span>
            </button>
          )}
        </>
      )}
      {presentation === "workspace" && !isCompactOutreach ? (
        <div className="socialPromptModal_footerCompleteWrap">
          {renderMarkCompleteButton("socialPromptModal_footerCompleteBtn")}
        </div>
      ) : null}
    </div>
  );

  const renderWorkspaceStepBtn = (prompt: ISocialPrompt) => {
    const stepNumber = getPromptStepNumber(prompt);
    const isActive = selectedPrompt?.id === prompt.id;
    const isCompleted =
      prompt.interaction_status.insight_status === INSIGHT_STATUS.COMPLETED;
    const contentLabel = prompt.content_type
      ? formatEnumValue(prompt.content_type)
      : "Prompt";

    return (
      <button
        key={prompt.id}
        type="button"
        className={[
          "outreachWorkspace_stepBtn",
          isActive ? "outreachWorkspace_stepBtn--active" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => handleStepClick(prompt)}
        aria-label={`Step ${stepNumber ?? "—"}: ${contentLabel}${isCompleted ? ", completed" : ""}`}
      >
        <span
          className={[
            "outreachWorkspace_stepNumber",
            isCompleted ? "outreachWorkspace_stepNumber--done" : "",
          ].filter(Boolean).join(" ")}
          aria-hidden
        >
          {isCompleted ? <i className="fa-solid fa-check" /> : stepNumber ?? "—"}
        </span>
        <span className="outreachWorkspace_stepIcon" aria-hidden>
          <i className={getContentTypeIconClass(prompt.content_type)} />
        </span>
        <span className="outreachWorkspace_stepLabel">{contentLabel}</span>
      </button>
    );
  };

  const renderWorkspaceSequenceRail = () => {
    const selectedSeriesCreatedLabel = selectedSeries
      ? formatSeriesCreatedAt(selectedSeries.created_at)
      : null;

    return (
    <aside className="outreachWorkspace_sequence">
      <div className="outreachWorkspace_panelHead outreachWorkspace_panelHead--stacked">
        <div className="outreachWorkspace_panelHeadRow outreachWorkspace_panelHead--withAction">
          <h2 className="outreachWorkspace_panelTitle">Sequence</h2>
          <button
            type="button"
            className="outreachWorkspace_requestMoreBtn"
            data-tour="social-prompt-request-more"
            onClick={() => handlePageChange("request")}
          >
            <i className="fa-solid fa-plus" aria-hidden /> New Sequence
          </button>
        </div>
      </div>
      <div className="outreachWorkspace_panelScroll">
        {filteredSeries.length > 1 ? (
          <div className="outreachWorkspace_seriesList">
            {filteredSeries.map((series) => {
              const isSelected = selectedSeriesId === series.id;
              const seriesTitle =
                compactSeriesDisplayTitles.get(series.id) ?? getSeriesDisplayTitle(series);
              const createdLabel = formatSeriesCreatedAt(series.created_at);

              return (
                <div
                  key={series.id}
                  className={[
                    "outreachWorkspace_seriesGroup",
                    isSelected ? "outreachWorkspace_seriesGroup--expanded" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className="outreachWorkspace_seriesBtnRow">
                    <button
                      type="button"
                      className={`outreachWorkspace_seriesBtn ${isSelected ? "outreachWorkspace_seriesBtn--active" : ""}`}
                      onClick={() => handleSeriesClick(series.id)}
                      aria-expanded={isSelected}
                    >
                      <span className="outreachWorkspace_seriesBtnTitleRow">
                        <span className="outreachWorkspace_seriesBtnTitle">
                          {seriesTitle}
                        </span>
                        {createdLabel ? (
                          <span className="outreachWorkspace_seriesBtnDate">
                            {createdLabel}
                          </span>
                        ) : null}
                      </span>
                    </button>
                    {isSelected && canAdministerPrompts ? (
                      <button
                        type="button"
                        className="outreachWorkspace_selectedSeriesDeleteBtn"
                        onClick={handleDelete}
                        aria-label={`Delete ${seriesTitle}`}
                        title={`Delete ${seriesTitle}`}
                      >
                        <i className="fa-solid fa-trash" aria-hidden />
                      </button>
                    ) : null}
                  </div>
                  {isSelected && stepsInSelectedSeries.length > 0 ? (
                    <nav
                      className="outreachWorkspace_steps outreachWorkspace_steps--nested"
                      data-tour="social-prompt-sequence"
                      aria-label={`Steps for ${seriesTitle}`}
                    >
                      {stepsInSelectedSeries.map(renderWorkspaceStepBtn)}
                    </nav>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : stepsInSelectedSeries.length > 0 ? (
          <>
            {selectedSeries ? (
              <div className="outreachWorkspace_seriesSingleHead">
                <span className="outreachWorkspace_seriesSingleTitle">
                  {getSeriesDisplayTitle(selectedSeries)}
                </span>
                {selectedSeriesCreatedLabel ? (
                  <span className="outreachWorkspace_seriesSingleDate">
                    {selectedSeriesCreatedLabel}
                  </span>
                ) : null}
                <p className="outreachWorkspace_seriesSingleMeta">
                  {getSeriesStepsMeta(selectedSeries)}
                </p>
              </div>
            ) : null}
            <nav
              className="outreachWorkspace_steps"
              data-tour="social-prompt-sequence"
              aria-label="Outreach steps"
            >
              {stepsInSelectedSeries.map(renderWorkspaceStepBtn)}
            </nav>
          </>
        ) : null}
      </div>
    </aside>
    );
  };

  const renderWorkspaceRecipientPanel = () => {
    const promptType = selectedPrompt?.content_type || "";
    const hint =
      "Pick who should receive this outreach. Contacts that don't match the channel are grayed out.";

    return (
      <aside className="outreachWorkspace_recipient">
        <div className="outreachWorkspace_panelHead outreachWorkspace_panelHead--stacked">
          <h2 className="outreachWorkspace_panelTitle">Recipient</h2>
          <p className="outreachWorkspace_panelHint">{hint}</p>
        </div>
        <div className="outreachWorkspace_panelScroll" ref={contactListRef}>
          {!localPossibleContacts?.length ? (
            <p className="outreachWorkspace_recipientEmpty">
              No contacts available for this insight. You can still copy or send outreach manually.
            </p>
          ) : (
            <div
              className="outreachWorkspace_contactList"
              data-tour="social-prompt-contacts"
            >
              {getContactsOrderedForChannel(promptType).map((contact) =>
                renderContactCard(contact, promptType)
              )}
            </div>
          )}
        </div>
      </aside>
    );
  };

  const renderSeriesRevision = () => allowPromptSave && selectedSeries ? (
    <SocialPromptRevisionPanel
      key={selectedSeries.id}
      series={selectedSeries}
      hasUnsavedChanges={!!selectedPrompt && (message !== (selectedPrompt.message ?? "") || subject !== (selectedPrompt.subject ?? ""))}
      onApplied={(changes) => {
        const changed = new Map(changes.map(change => [change.prompt_id, change]));
        setSocialPromptSeries(previous => previous.map(series => series.id !== selectedSeries.id ? series : {
          ...series,
          prompts: series.prompts.map(prompt => {
            const change = changed.get(prompt.id);
            return change ? { ...prompt, subject: change.subject, message: change.message } : prompt;
          }),
        }));
      }}
    />
  ) : null;

  const renderWorkspacePromptsPage = () => {
    if (isCompactOutreach) {
      return (
        <div className="outreachWorkspace_body outreachWorkspace_body--focus">
          <div
            className="outreachWorkspace_focusChrome"
            data-tour="social-prompt-sequence"
          >
            {renderPromptsContextBar(true)}
          </div>
          <div className="outreachWorkspace_focusScroll">
            <main className="outreachWorkspace_compose outreachWorkspace_compose--focus">
              <div className="outreachWorkspace_composeInner">
                {renderSeriesRevision()}
                <div
                  className="outreachWorkspace_composeUnit"
                  data-tour="social-prompt-compose"
                >
                  {renderComposeFields()}
                  {renderContactSection(
                    selectedPrompt?.content_type || "",
                    "Pick who should receive this outreach. Contacts that don't match the channel are grayed out.",
                  )}
                  {renderNotification()}
                </div>
              </div>
            </main>
          </div>
          <div className="outreachWorkspace_focusActions">
            {renderActionFooter()}
          </div>
        </div>
      );
    }

    return (
      <div className="outreachWorkspace_body">
        <div className="outreachWorkspace_grid">
          {renderWorkspaceSequenceRail()}
          <main className="outreachWorkspace_compose">
            <div className="outreachWorkspace_composeInner">
              {renderSeriesRevision()}
              <div
                className="outreachWorkspace_composeUnit"
                data-tour="social-prompt-compose"
              >
                {renderComposeFields()}
                {renderNotification()}
                {renderActionFooter()}
              </div>
            </div>
          </main>
          {renderWorkspaceRecipientPanel()}
        </div>
      </div>
    );
  };

  const renderWorkspaceRequestPage = () => {
    const wizardSteps = [
      { number: 1, label: "Setup", description: "Name and guide it" },
      { number: 2, label: "Cadence", description: "Build the outreach" },
      { number: 3, label: "Recipient (optional)", description: "Choose who it is for" },
    ] as const;
    const activeStepCopy = {
      1: {
        eyebrow: "Step 1 of 3",
        title: "Set up your sequence",
        hint: "Give it a name and tell us how it should sound. Everything here is optional.",
      },
      2: {
        eyebrow: "Step 2 of 3",
        title: "Build your cadence",
        hint:
          category === "CONTACT_INTELLIGENCE"
            ? "Choose a channel and add a short optional goal for each touch."
            : "Choose a channel and add a short optional goal for each touch. Emails, calls, and LinkedIn DMs can be combined; LinkedIn posts and blogs must be used on their own.",
      },
      3: {
        eyebrow: "Step 3 of 3",
        title: "Choose a recipient",
        hint: cadencesEnabled
          ? "Choose one contact to personalize this sequence, or continue without one."
          : "Choose one contact to personalize this prompt, or continue without one.",
      },
    }[requestWizardStep];

    return (
      <div ref={requestWizardRef} className="outreachWorkspace_body outreachWorkspace_body--request">
        <div className="outreachWorkspace_wizard">
          <div className="outreachWorkspace_wizardTop">
            <div>
              <span className="outreachWorkspace_wizardKicker">New outreach sequence</span>
              <h2 className="outreachWorkspace_wizardTitle">Create a sequence</h2>
            </div>
          </div>

          <nav className="outreachWorkspace_wizardProgress" aria-label="Create sequence progress">
            {wizardSteps.map((step, index) => {
              const isActive = requestWizardStep === step.number;
              const isComplete = requestWizardStep > step.number;
              return (
                <Fragment key={step.number}>
                  <button
                    type="button"
                    className={[
                      "outreachWorkspace_wizardProgressStep",
                      isActive ? "outreachWorkspace_wizardProgressStep--active" : "",
                      isComplete ? "outreachWorkspace_wizardProgressStep--complete" : "",
                    ].filter(Boolean).join(" ")}
                    onClick={() => setRequestWizardStep(step.number)}
                    aria-current={isActive ? "step" : undefined}
                  >
                    <span className="outreachWorkspace_wizardProgressNumber">
                      {isComplete ? <i className="fa-solid fa-check" aria-hidden /> : step.number}
                    </span>
                    <span className="outreachWorkspace_wizardProgressCopy">
                      <strong>{step.label}</strong>
                      <small>{step.description}</small>
                    </span>
                  </button>
                  {index < wizardSteps.length - 1 ? (
                    <span className="outreachWorkspace_wizardProgressLine" aria-hidden />
                  ) : null}
                </Fragment>
              );
            })}
          </nav>

          <section className="outreachWorkspace_wizardCard">
            <header className="outreachWorkspace_wizardCardHead">
              <div>
                <div className="outreachWorkspace_wizardMetaRow">
                  <span className="outreachWorkspace_wizardStepLabel">{activeStepCopy.eyebrow}</span>
                  {requestWizardStep === 2 && cadencesEnabled ? (
                    <button
                      type="button"
                      className="outreachWorkspace_requestTemplateBtn outreachWorkspace_wizardHeaderAction"
                      onClick={openSaveTemplateDialog}
                    >
                      <i className="fa-regular fa-bookmark" aria-hidden /> Save template
                    </button>
                  ) : null}
                </div>
                <div className="outreachWorkspace_wizardCardTitleRow">
                  <h3>{activeStepCopy.title}</h3>
                  {requestWizardStep === 3 && localPossibleContacts?.length ? (
                    <span className="outreachWorkspace_wizardSelection">
                      {selectedContact ? selectedContact.name : "No recipient selected"}
                    </span>
                  ) : null}
                </div>
                <p>{activeStepCopy.hint}</p>
                {requestWizardStep === 2 && cadencesEnabled && templates.length > 0 ? (
                  <div className="outreachWorkspace_wizardTemplatePicker">
                    <div className="outreachWorkspace_requestDropdown">
                      <CustomDropdown
                        key={`workspace-template-${selectedTemplateId}`}
                        options={[
                          { value: "", label: "Start from a saved template (optional)" },
                          ...templates.map((template) => ({
                            value: template.id,
                            label: `${template.title} (${template.step_count} step${
                              template.step_count !== 1 ? "s" : ""
                            })`,
                          })),
                        ]}
                        onChange={handleApplyTemplate}
                        defaultOptionIndex={Math.max(
                          0,
                          templates.findIndex((t) => t.id === selectedTemplateId) + 1
                        )}
                        portaledMenu
                        menuClassName="outreachWorkspace_wizardOptionsMenu"
                        testId="workspace-template-dropdown-select"
                      />
                    </div>
                    {isLoadingTemplate ? (
                      <p className="outreachWorkspace_requestHint">
                        <i className="fa-solid fa-spinner fa-spin" aria-hidden /> Loading template…
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </header>

            <div className="outreachWorkspace_wizardContent">
              {requestWizardStep === 1 ? (
                <div
                  className="outreachWorkspace_wizardSetup"
                  data-tour="social-prompt-request-setup"
                >
                  <div className="outreachWorkspace_requestField">
                    <label className="outreachWorkspace_requestFieldLabel" htmlFor="workspace-sequence-title">
                      Sequence title <span>Optional</span>
                    </label>
                    <input
                      id="workspace-sequence-title"
                      className="outreachWorkspace_requestInput"
                      value={sequenceTitle}
                      onChange={(e) => setSequenceTitle(e.target.value)}
                      placeholder="Example: Executive follow-up"
                      maxLength={255}
                    />
                  </div>

                  <div className="outreachWorkspace_requestField">
                    <label className="outreachWorkspace_requestFieldLabel" htmlFor="workspace-voice-picker">
                      Voice style <span>Optional</span>
                    </label>
                    <div className="outreachWorkspace_requestDropdown" id="workspace-voice-picker">
                      <CustomDropdown
                        key={`workspace-voice-${selectedVoice}`}
                        options={[
                          { value: "", label: "Use your default voice" },
                          ...availableVoices.map((voice) => ({
                            value: voice.key,
                            label: voice.label,
                          })),
                        ]}
                        onChange={(value) => setSelectedVoice(value)}
                        defaultOptionIndex={Math.max(
                          0,
                          availableVoices.findIndex((voice) => voice.key === selectedVoice) + 1
                        )}
                        portaledMenu
                        menuClassName="outreachWorkspace_wizardOptionsMenu"
                        testId="workspace-voice-dropdown-select"
                      />
                    </div>
                    {selectedVoice &&
                    availableVoices.find((voice) => voice.key === selectedVoice)?.description ? (
                      <p className="outreachWorkspace_requestHint">
                        {availableVoices.find((voice) => voice.key === selectedVoice)?.description}
                      </p>
                    ) : null}
                  </div>

                  <div className="outreachWorkspace_requestField outreachWorkspace_requestField--wide">
                    <label className="outreachWorkspace_requestFieldLabel" htmlFor="workspace-request-context">
                      Extra context <span>Optional</span>
                    </label>
                    <textarea
                      id="workspace-request-context"
                      className="outreachWorkspace_requestTextarea"
                      value={additionalContext}
                      onChange={(e) => setAdditionalContext(e.target.value)}
                      placeholder="Add a specific angle, goal, or detail you want the AI to use."
                      rows={4}
                    />
                  </div>
                </div>
              ) : null}

              {requestWizardStep === 2 ? (
                <div
                  className="outreachWorkspace_requestCadence outreachWorkspace_wizardCadence"
                  data-tour="social-prompt-request-cadence"
                >
                  <CadenceStepEditor
                    steps={cadenceSteps}
                    onChange={setCadenceSteps}
                    excludeSoloChannels={category === "CONTACT_INTELLIGENCE"}
                    maxSteps={cadenceMaxSteps}
                    channelControl="pills"
                  />
                </div>
              ) : null}

              {requestWizardStep === 3 ? (
                <div className="outreachWorkspace_wizardRecipients">
                  {!localPossibleContacts?.length ? (
                    <div className="outreachWorkspace_wizardEmpty">
                      <i className="fa-regular fa-user" aria-hidden />
                      <strong>No contacts available</strong>
                      <p>You can still create this sequence without choosing a recipient.</p>
                    </div>
                  ) : (
                    <div
                      className="outreachWorkspace_requestContacts"
                      data-tour="social-prompt-request-recipient"
                    >
                      {getContactsOrderedForChannel(cadenceSteps).map((contact) =>
                        renderContactCard(contact, cadenceSteps)
                      )}
                    </div>
                  )}
                </div>
              ) : null}

              {renderNotification()}
            </div>

            <footer className="outreachWorkspace_wizardFooter">
              <div />
              <div className="outreachWorkspace_wizardFooterActions">
                {requestWizardStep < 3 ? (
                  <button
                    type="button"
                    className="outreachWorkspace_wizardPrimaryBtn"
                    onClick={() => setRequestWizardStep((requestWizardStep + 1) as 2 | 3)}
                  >
                    {requestWizardStep === 1 ? "Continue to cadence" : "Choose recipient"}
                    <i className="fa-solid fa-arrow-right" aria-hidden />
                  </button>
                ) : (
                  <div data-tour="social-prompt-request">
                    {renderRequestSubmitButton({
                      className: "outreachWorkspace_requestSubmitBtn",
                      label: cadencesEnabled ? "Create sequence" : "Create prompt",
                    })}
                  </div>
                )}
              </div>
            </footer>
          </section>
        </div>
      </div>
    );
  };

  const renderPromptLoadingState = (workspace = false) => (
    <div
      className={
        workspace
          ? "outreachWorkspace_body outreachWorkspace_loading"
          : "socialPromptModal_promptLoading"
      }
      role="status"
      aria-live="polite"
    >
      <div className="outreachWorkspace_loadingContent">
        <span className="outreachWorkspace_loadingSpinner" aria-hidden>
          <i className="fa-solid fa-spinner fa-spin" />
        </span>
        <strong>Loading outreach</strong>
        <span>Checking for existing sequences…</span>
      </div>
    </div>
  );

  const renderWorkspaceEntryChoices = () => (
    <div className="outreachWorkspace_body outreachWorkspace_body--entry">
      <section
        className="outreachWorkspace_entryCard"
        aria-labelledby="outreach-workspace-entry-title"
      >
        <p className="outreachWorkspace_entryKicker">Choose a starting point</p>
        <h2
          id="outreach-workspace-entry-title"
          className="outreachWorkspace_entryTitle"
        >
          What would you like to do?
        </h2>
        <p className="outreachWorkspace_entryLead">
          Start a fresh outreach sequence or open the sequences that are ready
          to use.
        </p>
        <div className="outreachWorkspace_entryChoices">
          <button
            type="button"
            className="outreachWorkspace_entryChoice outreachWorkspace_entryChoice--primary"
            onClick={() => handlePageChange("request")}
            aria-label="Create new sequence"
          >
            <span className="outreachWorkspace_entryChoiceIcon" aria-hidden>
              <i className="fa-solid fa-plus" />
            </span>
            <span className="outreachWorkspace_entryChoiceText">
              <span className="outreachWorkspace_entryChoiceTitle">
                Create new sequence
              </span>
              <span className="outreachWorkspace_entryChoiceDescription">
                Choose the channels, cadence, and recipient for new outreach.
              </span>
            </span>
            <i
              className="fa-solid fa-chevron-right outreachWorkspace_entryChoiceArrow"
              aria-hidden
            />
          </button>
          <button
            type="button"
            className="outreachWorkspace_entryChoice"
            onClick={() => handlePageChange("prompts")}
            aria-label="See live sequences"
            disabled={prompts.length === 0}
          >
            <span className="outreachWorkspace_entryChoiceIcon" aria-hidden>
              <i className="fa-solid fa-list-check" />
            </span>
            <span className="outreachWorkspace_entryChoiceText">
              <span className="outreachWorkspace_entryChoiceTitle">
                See live sequences
              </span>
              <span className="outreachWorkspace_entryChoiceDescription">
                {prompts.length > 0
                  ? "Review, personalize, and send the outreach already available."
                  : "No live sequences are available yet."}
              </span>
            </span>
            <i
              className="fa-solid fa-chevron-right outreachWorkspace_entryChoiceArrow"
              aria-hidden
            />
          </button>
        </div>
      </section>
    </div>
  );

  const renderWorkspaceRequestConfirmation = () => (
    <div className="outreachWorkspace_body outreachWorkspace_body--entry">
      <section
        className="outreachWorkspace_entryCard outreachWorkspace_confirmationCard"
        aria-labelledby="outreach-workspace-confirmation-title"
      >
        <span className="outreachWorkspace_confirmationIcon" aria-hidden>
          <i className="fa-solid fa-circle-check" />
        </span>
        <p className="outreachWorkspace_entryKicker">Sequence requested</p>
        <h2
          id="outreach-workspace-confirmation-title"
          className="outreachWorkspace_entryTitle"
        >
          We're creating your sequence
        </h2>
        <p className="outreachWorkspace_entryLead">
          This will be created soon. You'll be notified.
        </p>
        <button
          type="button"
          className="outreachWorkspace_wizardPrimaryBtn outreachWorkspace_confirmationClose"
          onClick={onClose}
        >
          Close
        </button>
      </section>
    </div>
  );

  const renderWorkspaceShell = () => (
    <div
      className={[
        "outreachWorkspace",
        isCompactOutreach ? "outreachWorkspace--compact" : "",
        getSocialPromptGlassModifier(category),
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <header className="outreachWorkspace_topBar">
        <div className="outreachWorkspace_topBarStart">
          <button
            type="button"
            className="outreachWorkspace_backBtn"
            onClick={handleWorkspaceBack}
            aria-label="Back"
          >
            <i className="fa-solid fa-arrow-left" aria-hidden />
            Back
          </button>
          <div className="outreachWorkspace_heading" aria-label="Insight context">
            {renderWorkspaceHeadingContent()}
          </div>
        </div>
        <div className="outreachWorkspace_topBarEnd">
          <button
            type="button"
            className="outreachWorkspace_dismissBtn"
            onClick={onClose}
            aria-label="Close outreach workspace"
            title="Close"
          >
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>
      </header>
      {!promptViewReady
        ? renderPromptLoadingState(true)
        : requestSubmitted
          ? renderWorkspaceRequestConfirmation()
        : showEntryChoices
          ? renderWorkspaceEntryChoices()
        : currentPage === "prompts"
          ? renderWorkspacePromptsPage()
          : renderWorkspaceRequestPage()}
    </div>
  );

  const renderSaveTemplateDialog = () => {
    if (!isSaveTemplateOpen) return null;
    return (
      <>
        <button
          type="button"
          className="socialPromptModal_confirmDialogOverlay"
          aria-label="Dismiss"
          onClick={closeSaveTemplateDialog}
        />
        <div
          className="socialPromptModal_confirmDialog socialPromptModal_saveTemplateDialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="socialPrompt-save-template-title"
        >
          <p
            id="socialPrompt-save-template-title"
            className="socialPromptModal_confirmDialogMessage"
          >
            Save this cadence as a reusable template.
          </p>
          <div className="socialPromptModal_textBoxSection">
            <label
              className="socialPromptModal_sectionTitleText"
              htmlFor="socialPrompt-save-template-input"
            >
              Template title
            </label>
            <input
              id="socialPrompt-save-template-input"
              className="socialPromptModal_sequenceTitleInput"
              value={saveTemplateTitle}
              onChange={(e) => {
                setSaveTemplateTitle(e.target.value);
                if (saveTemplateError) setSaveTemplateError(null);
                if (duplicateTemplateId) setDuplicateTemplateId(null);
              }}
              placeholder="e.g. 3-touch exec outreach"
              maxLength={255}
              autoFocus
            />
          </div>
          {saveTemplateError && (
            <div
              className="socialPromptModal_notice socialPromptModal_notice--error"
              role="alert"
            >
              <span className="fa-solid fa-circle-exclamation" aria-hidden />
              <span>{saveTemplateError}</span>
            </div>
          )}
          <div className="socialPromptModal_confirmDialogButtons">
            <button
              type="button"
              className="socialPromptModal_confirmDialogButton socialPromptModal_confirmDialogButton--secondary"
              onClick={closeSaveTemplateDialog}
              disabled={isSavingTemplate}
            >
              Cancel
            </button>
            {duplicateTemplateId && (
              <button
                type="button"
                className="socialPromptModal_confirmDialogButton socialPromptModal_confirmDialogButton--secondary"
                onClick={handleUpdateExistingTemplate}
                disabled={isSavingTemplate}
              >
                Update existing
              </button>
            )}
            <button
              type="button"
              className="socialPromptModal_confirmDialogButton socialPromptModal_confirmDialogButton--primary"
              onClick={handleSaveTemplate}
              disabled={isSavingTemplate}
            >
              {isSavingTemplate ? "Saving…" : "Save template"}
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderCompleteConfirm = () => {
    if (!completeConfirmOpen) return null;
    return (
      <>
        <button
          type="button"
          className="socialPromptModal_confirmDialogOverlay"
          aria-label="Dismiss"
          onClick={() => setCompleteConfirmOpen(false)}
        />
        <div
          className="socialPromptModal_confirmDialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="socialPrompt-complete-confirm-title"
        >
          <p
            id="socialPrompt-complete-confirm-title"
            className="socialPromptModal_confirmDialogMessage"
          >
            No contact is selected. Would you like to select a contact before completing this
            prompt?
          </p>
          <div className="socialPromptModal_confirmDialogButtons">
            <button
              type="button"
              className="socialPromptModal_confirmDialogButton socialPromptModal_confirmDialogButton--secondary"
              onClick={() => {
                setCompleteConfirmOpen(false);
                runMarkPromptComplete();
              }}
            >
              Complete without contact
            </button>
            <button
              type="button"
              className="socialPromptModal_confirmDialogButton socialPromptModal_confirmDialogButton--primary"
              onClick={() => setCompleteConfirmOpen(false)}
            >
              Select contact
            </button>
          </div>
        </div>
      </>
    );
  };

  const deleteSeriesConfirmMessage = selectedSeries
    ? `Delete this outreach sequence? All ${selectedSeries.total_steps} step${
        selectedSeries.total_steps !== 1 ? "s" : ""
      } will be permanently removed.`
    : "Delete this outreach sequence? All steps will be permanently removed.";

  const renderDeleteSeriesConfirm = () => {
    if (!showDeleteSeriesConfirm) return null;

    return (
      <>
        <button
          type="button"
          className="socialPromptModal_confirmDialogOverlay"
          aria-label="Dismiss"
          onClick={() => {
            if (!isDeletingSeries) {
              setShowDeleteSeriesConfirm(false);
            }
          }}
        />
        <div
          className="socialPromptModal_confirmDialog"
          role="dialog"
          aria-modal="true"
          aria-label="Delete outreach series"
          aria-labelledby="socialPrompt-delete-series-confirm-title"
        >
          <p
            id="socialPrompt-delete-series-confirm-title"
            className="socialPromptModal_confirmDialogMessage"
          >
            {deleteSeriesConfirmMessage}
          </p>
          <p className="socialPromptModal_confirmDialogWarning">
            This action cannot be undone.
          </p>
          <div className="socialPromptModal_confirmDialogButtons">
            <button
              type="button"
              className="socialPromptModal_confirmDialogButton socialPromptModal_confirmDialogButton--secondary"
              onClick={() => setShowDeleteSeriesConfirm(false)}
              disabled={isDeletingSeries}
            >
              Cancel
            </button>
            <button
              type="button"
              className="socialPromptModal_confirmDialogButton socialPromptModal_confirmDialogButton--danger"
              onClick={handleConfirmDeleteSeries}
              disabled={isDeletingSeries}
            >
              {isDeletingSeries ? "Deleting…" : "Delete sequence"}
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderOverlays = () => (
    <>
      {renderDeleteSeriesConfirm()}
      {renderCompleteConfirm()}
      {renderSaveTemplateDialog()}
      {tooltip.visible && (
        <div
          className="socialPromptModal_authorTooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltip.text}
        </div>
      )}
    </>
  );

  if (presentation === "workspace") {
    return (
      <>
        {renderWorkspaceShell()}
        {renderOverlays()}
      </>
    );
  }

  return (
    <>
      <div
        className={[
          "socialPromptModal_container",
          "glassModal_shell",
          getSocialPromptGlassModifier(category),
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <GlassModalHeader
          kicker={getSocialPromptKicker(category)}
          title="Share Insight"
          subtitle={headerSubtitle}
          trailingActions={renderHeaderTrailingActions()}
          onClose={onClose}
          closeAriaLabel="Close share insight"
        />
        <div className="socialPromptModal_body">
          {!promptViewReady
            ? renderPromptLoadingState()
            : currentPage === "prompts"
              ? renderPromptsPage()
              : renderRequestPage()}
        </div>
      </div>
      {renderOverlays()}
    </>
  );
};
